import { useReducer, useState, useCallback, useMemo, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Link, useParams, useNavigate } from 'react-router';
import {
  ArrowLeft,
  Sparkles,
  Activity,
  TrendingUp,
  XCircle,
  Grid3x3,
  Shield,
  Crosshair,
  Clock,
  Target,
  Plus,
  X,
  Search,
  DollarSign,
  BarChart3,
  Save,
  Layers,
  TrendingDown,
  Box,
  Code,
  RefreshCcw,
  ScissorsLineDashed,
  HelpCircle,
  MessageSquare,
  Bot,
  Upload,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { Modal } from '@/components/ui/modal';
import { useStrategy, useCreateStrategy, useUpdateConfig, useUpdateStrategy } from '@/hooks/use-strategies';
import {
  STRATEGY_TEMPLATES,
  type StrategyTemplatePreset,
} from '@/components/strategy-editor/strategy-templates';
import { AccordionSection } from '@/components/strategy-editor/accordion-section';
import { EditorField, EdInput } from '@/components/strategy-editor/editor-field';
import { LotSizingEditor } from '@/components/strategy-editor/lot-sizing-editor';
import { GridSection } from '@/components/strategy-editor/grid-section';
import { TrailingStopSection } from '@/components/strategy-editor/trailing-stop-section';
import {
  draftReducer,
  TIMEFRAMES,
  OPS,
  OP_LABELS,
  getByPath,
  setByPathImmutable,
  type ConditionEngineRules,
  type DraftAction,
  type Condition,
  type ComparisonCondition,
  type ConditionGroup,
  type ValueRef,
} from '@/components/strategy-editor/editor-types';
import { condToSentence } from '@/lib/condition-text';
import { SymbolPicker } from '@/components/ui/symbol-picker';
import { EditorWalkthrough } from '@/components/strategy-editor/editor-walkthrough';
import { StrategyChatPanel } from '@/components/strategy-editor/strategy-chat-panel';

// ─── Constants ─────────────────────────────────────────────


const INDICATOR_CATEGORIES: Record<string, { value: string; label: string }[]> = {
  Trend: [
    { value: 'SMA', label: 'Simple MA' },
    { value: 'EMA', label: 'Exponential MA' },
    { value: 'WMA', label: 'Weighted MA' },
    { value: 'DEMA', label: 'Double EMA' },
    { value: 'TEMA', label: 'Triple EMA' },
    { value: 'HMA', label: 'Hull MA' },
    { value: 'ADX', label: 'ADX' },
    { value: 'DMI_PLUS', label: 'DMI+' },
    { value: 'DMI_MINUS', label: 'DMI−' },
    { value: 'AROON_UP', label: 'Aroon Up' },
    { value: 'AROON_DOWN', label: 'Aroon Down' },
    { value: 'AROON_OSC', label: 'Aroon Oscillator' },
    { value: 'PSAR', label: 'Parabolic SAR' },
    { value: 'SUPERTREND', label: 'Supertrend' },
    { value: 'KST', label: 'KST' },
    { value: 'LINEAR_REG_SLOPE', label: 'Linear Reg Slope' },
  ],
  Momentum: [
    { value: 'RSI', label: 'RSI' },
    { value: 'MACD', label: 'MACD Line' },
    { value: 'MACD_SIGNAL', label: 'MACD Signal' },
    { value: 'MACD_HISTOGRAM', label: 'MACD Histogram' },
    { value: 'PPO', label: 'PPO' },
    { value: 'STOCH_K', label: 'Stochastic %K' },
    { value: 'STOCH_D', label: 'Stochastic %D' },
    { value: 'STOCH_RSI', label: 'Stochastic RSI' },
    { value: 'CCI', label: 'CCI' },
    { value: 'WILLIAMS_R', label: 'Williams %R' },
    { value: 'ROC', label: 'Rate of Change' },
    { value: 'MOMENTUM', label: 'Momentum' },
    { value: 'AWESOME_OSC', label: 'Awesome Oscillator' },
    { value: 'TRIX', label: 'TRIX' },
    { value: 'DPO', label: 'DPO' },
    { value: 'CMO', label: 'CMO' },
    { value: 'ULTIMATE_OSC', label: 'Ultimate Oscillator' },
    { value: 'COPPOCK', label: 'Coppock Curve' },
  ],
  Volatility: [
    { value: 'ATR', label: 'ATR' },
    { value: 'BB_UPPER', label: 'Bollinger Upper' },
    { value: 'BB_MIDDLE', label: 'Bollinger Middle' },
    { value: 'BB_LOWER', label: 'Bollinger Lower' },
    { value: 'BB_STD', label: 'Bollinger StdDev' },
    { value: 'KELTNER_UPPER', label: 'Keltner Upper' },
    { value: 'KELTNER_MID', label: 'Keltner Mid' },
    { value: 'KELTNER_LOWER', label: 'Keltner Lower' },
    { value: 'DONCHIAN_UPPER', label: 'Donchian Upper' },
    { value: 'DONCHIAN_LOWER', label: 'Donchian Lower' },
    { value: 'DONCHIAN_MID', label: 'Donchian Mid' },
    { value: 'CHANDELIER_EXIT_LONG', label: 'Chandelier Long' },
    { value: 'CHANDELIER_EXIT_SHORT', label: 'Chandelier Short' },
  ],
  'Range & Volume': [
    { value: 'RANGE_HIGH', label: 'Range High' },
    { value: 'RANGE_LOW', label: 'Range Low' },
    { value: 'RANGE_WIDTH', label: 'Range Width' },
    { value: 'HIGHEST_HIGH', label: 'Highest High' },
    { value: 'LOWEST_LOW', label: 'Lowest Low' },
    { value: 'VOLUME_SMA', label: 'Volume MA' },
    { value: 'OBV', label: 'On Balance Volume' },
    { value: 'VWAP', label: 'VWAP' },
    { value: 'MFI', label: 'Money Flow Index' },
    { value: 'CMF', label: 'Chaikin Money Flow' },
    { value: 'ADL', label: 'Accumulation/Dist' },
    { value: 'FORCE_INDEX', label: 'Force Index' },
  ],
  Ichimoku: [
    { value: 'ICHIMOKU_TENKAN', label: 'Tenkan-sen' },
    { value: 'ICHIMOKU_KIJUN', label: 'Kijun-sen' },
    { value: 'ICHIMOKU_SENKOU_A', label: 'Senkou A' },
    { value: 'ICHIMOKU_SENKOU_B', label: 'Senkou B' },
    { value: 'ICHIMOKU_CHIKOU', label: 'Chikou Span' },
  ],
  'Elder Ray & Pivots': [
    { value: 'ELDER_RAY_BULL', label: 'Elder Ray Bull' },
    { value: 'ELDER_RAY_BEAR', label: 'Elder Ray Bear' },
    { value: 'PIVOT_PP', label: 'Pivot Point' },
    { value: 'PIVOT_R1', label: 'Pivot R1' },
    { value: 'PIVOT_S1', label: 'Pivot S1' },
    { value: 'PIVOT_R2', label: 'Pivot R2' },
    { value: 'PIVOT_S2', label: 'Pivot S2' },
  ],
  'Price Derivatives': [
    { value: 'MEDIAN_PRICE', label: 'Median Price' },
    { value: 'TYPICAL_PRICE', label: 'Typical Price' },
    { value: 'WEIGHTED_CLOSE', label: 'Weighted Close' },
  ],
  'Candle Analysis': [
    { value: 'CANDLE_RANGE', label: 'Candle Range' },
    { value: 'CANDLE_BODY', label: 'Candle Body' },
    { value: 'SPIKE_RATIO', label: 'Spike Ratio' },
  ],
};

const PRICE_OPTIONS = [
  { value: 'CURRENT', label: 'Current Price' },
  { value: 'OPEN', label: 'Candle Open' },
  { value: 'HIGH', label: 'Candle High' },
  { value: 'LOW', label: 'Candle Low' },
  { value: 'CLOSE', label: 'Candle Close' },
  { value: 'VOLUME', label: 'Volume' },
  { value: 'PREV_TICK_HIGH', label: 'Prev Tick High' },
  { value: 'PREV_TICK_LOW', label: 'Prev Tick Low' },
];

const POSITION_OPTIONS = [
  { value: 'COUNT', label: 'Open Count' },
  { value: 'FLOATING_PNL', label: 'Floating P&L ($)' },
  { value: 'FLOATING_PNL_PCT', label: 'Floating P&L (%)' },
  { value: 'PNL_PER_LOT', label: 'P&L per Lot Unit ($)' },
  { value: 'LAST_ENTRY_PRICE', label: 'Last Entry Price' },
  { value: 'AVG_ENTRY_PRICE', label: 'Avg Entry Price' },
  { value: 'BASKET_VOLUME', label: 'Total Volume' },
  { value: 'TICKS_SINCE_ENTRY', label: 'Ticks Since Entry' },
  { value: 'CANDLES_SINCE_ENTRY', label: 'Candles Since Entry' },
];

const ACCOUNT_OPTIONS = [
  { value: 'EQUITY', label: 'Equity' },
  { value: 'BALANCE', label: 'Balance' },
  { value: 'ASSIGNED_CAPITAL', label: 'Assigned Capital' },
];

const TIME_OPTIONS = [
  { value: 'HOUR', label: 'Hour (0-23)' },
  { value: 'MINUTE', label: 'Minute (0-59)' },
  { value: 'DAY_OF_WEEK', label: 'Day of Week (0=Sun)' },
  { value: 'MONTH', label: 'Month (1-12)' },
];

// ─── Condition Presets ────────────────────────────────────

type PresetCategory = 'indicators' | 'price' | 'session' | 'position' | 'account';

interface ConditionPreset {
  id: string;
  name: string;
  desc: string;
  tip: string;
  category: PresetCategory;
  subCategory?: string;
  condition: Condition;
}

const PRESETS: ConditionPreset[] = [
  // Indicators — Momentum
  {
    id: 'rsi-oversold', name: 'RSI Oversold', desc: 'RSI < 30', tip: 'Detects when selling pressure is exhausted \u2014 classic buy signal for reversals.', category: 'indicators', subCategory: 'Momentum',
    condition: { type: 'COMPARISON', left: { type: 'INDICATOR', indicator: 'RSI', params: { period: 14 } }, op: '<', right: { type: 'CONSTANT', value: 30 } },
  },
  {
    id: 'rsi-overbought', name: 'RSI Overbought', desc: 'RSI > 70', tip: 'Detects when buying pressure is exhausted \u2014 classic sell signal for reversals.', category: 'indicators', subCategory: 'Momentum',
    condition: { type: 'COMPARISON', left: { type: 'INDICATOR', indicator: 'RSI', params: { period: 14 } }, op: '>', right: { type: 'CONSTANT', value: 70 } },
  },
  {
    id: 'macd-bull-cross', name: 'MACD Bull Cross', desc: 'MACD crosses above Signal', tip: 'Signals upward momentum shift \u2014 stronger when confirmed by trend direction.', category: 'indicators', subCategory: 'Momentum',
    condition: { type: 'COMPARISON', left: { type: 'INDICATOR', indicator: 'MACD', params: { period: 26, fastPeriod: 12, slowPeriod: 26, signalPeriod: 9 } }, op: 'CROSSES_ABOVE', right: { type: 'INDICATOR', indicator: 'MACD_SIGNAL', params: { period: 26, fastPeriod: 12, slowPeriod: 26, signalPeriod: 9 } } },
  },
  {
    id: 'macd-bear-cross', name: 'MACD Bear Cross', desc: 'MACD crosses below Signal', tip: 'Signals downward momentum shift \u2014 stronger when confirmed by trend direction.', category: 'indicators', subCategory: 'Momentum',
    condition: { type: 'COMPARISON', left: { type: 'INDICATOR', indicator: 'MACD', params: { period: 26, fastPeriod: 12, slowPeriod: 26, signalPeriod: 9 } }, op: 'CROSSES_BELOW', right: { type: 'INDICATOR', indicator: 'MACD_SIGNAL', params: { period: 26, fastPeriod: 12, slowPeriod: 26, signalPeriod: 9 } } },
  },
  {
    id: 'stoch-oversold', name: 'Stoch Oversold', desc: 'Stochastic %K < 20', tip: 'Fast oscillator showing extreme selling \u2014 good for short-term bounce entries.', category: 'indicators', subCategory: 'Momentum',
    condition: { type: 'COMPARISON', left: { type: 'INDICATOR', indicator: 'STOCH_K', params: { period: 14, signalPeriod: 3 } }, op: '<', right: { type: 'CONSTANT', value: 20 } },
  },
  {
    id: 'stoch-overbought', name: 'Stoch Overbought', desc: 'Stochastic %K > 80', tip: 'Fast oscillator showing extreme buying \u2014 good for short-term pullback entries.', category: 'indicators', subCategory: 'Momentum',
    condition: { type: 'COMPARISON', left: { type: 'INDICATOR', indicator: 'STOCH_K', params: { period: 14, signalPeriod: 3 } }, op: '>', right: { type: 'CONSTANT', value: 80 } },
  },
  {
    id: 'stoch-rsi-oversold', name: 'StochRSI Oversold', desc: 'StochRSI < 20', tip: 'Combines RSI with Stochastic for extra sensitivity to oversold conditions.', category: 'indicators', subCategory: 'Momentum',
    condition: { type: 'COMPARISON', left: { type: 'INDICATOR', indicator: 'STOCH_RSI', params: { period: 14 } }, op: '<', right: { type: 'CONSTANT', value: 20 } },
  },
  {
    id: 'stoch-rsi-overbought', name: 'StochRSI Overbought', desc: 'StochRSI > 80', tip: 'Combines RSI with Stochastic for extra sensitivity to overbought conditions.', category: 'indicators', subCategory: 'Momentum',
    condition: { type: 'COMPARISON', left: { type: 'INDICATOR', indicator: 'STOCH_RSI', params: { period: 14 } }, op: '>', right: { type: 'CONSTANT', value: 80 } },
  },
  {
    id: 'cci-oversold', name: 'CCI Oversold', desc: 'CCI < -100', tip: 'Measures price deviation from average \u2014 extreme low suggests a reversal up.', category: 'indicators', subCategory: 'Momentum',
    condition: { type: 'COMPARISON', left: { type: 'INDICATOR', indicator: 'CCI', params: { period: 20 } }, op: '<', right: { type: 'CONSTANT', value: -100 } },
  },
  {
    id: 'cci-overbought', name: 'CCI Overbought', desc: 'CCI > 100', tip: 'Measures price deviation from average \u2014 extreme high suggests a reversal down.', category: 'indicators', subCategory: 'Momentum',
    condition: { type: 'COMPARISON', left: { type: 'INDICATOR', indicator: 'CCI', params: { period: 20 } }, op: '>', right: { type: 'CONSTANT', value: 100 } },
  },
  {
    id: 'williams-oversold', name: 'Williams %R Oversold', desc: '%R < -80', tip: 'Similar to RSI but inverted scale — values near -100 signal oversold.', category: 'indicators', subCategory: 'Momentum',
    condition: { type: 'COMPARISON', left: { type: 'INDICATOR', indicator: 'WILLIAMS_R', params: { period: 14 } }, op: '<', right: { type: 'CONSTANT', value: -80 } },
  },
  {
    id: 'williams-overbought', name: 'Williams %R Overbought', desc: '%R > -20', tip: 'Similar to RSI but inverted scale — values near 0 signal overbought.', category: 'indicators', subCategory: 'Momentum',
    condition: { type: 'COMPARISON', left: { type: 'INDICATOR', indicator: 'WILLIAMS_R', params: { period: 14 } }, op: '>', right: { type: 'CONSTANT', value: -20 } },
  },
  {
    id: 'roc-positive', name: 'ROC Positive', desc: 'ROC > 0 (momentum up)', tip: 'Price rate of change is positive — confirms upward momentum.', category: 'indicators', subCategory: 'Momentum',
    condition: { type: 'COMPARISON', left: { type: 'INDICATOR', indicator: 'ROC', params: { period: 12 } }, op: '>', right: { type: 'CONSTANT', value: 0 } },
  },
  {
    id: 'momentum-positive', name: 'Momentum Up', desc: 'Momentum > 0', tip: 'Raw momentum above zero — price is trending upward.', category: 'indicators', subCategory: 'Momentum',
    condition: { type: 'COMPARISON', left: { type: 'INDICATOR', indicator: 'MOMENTUM', params: { period: 10 } }, op: '>', right: { type: 'CONSTANT', value: 0 } },
  },
  {
    id: 'awesome-positive', name: 'Awesome Osc +', desc: 'AO > 0 (bullish)', tip: 'Awesome Oscillator positive — market momentum favors buyers.', category: 'indicators', subCategory: 'Momentum',
    condition: { type: 'COMPARISON', left: { type: 'INDICATOR', indicator: 'AWESOME_OSC', params: { period: 14, fastPeriod: 5, slowPeriod: 34 } }, op: '>', right: { type: 'CONSTANT', value: 0 } },
  },
  {
    id: 'trix-cross-up', name: 'TRIX Cross Up', desc: 'TRIX × above 0', tip: 'Triple-smoothed EMA crossing zero — filters noise for cleaner signals.', category: 'indicators', subCategory: 'Momentum',
    condition: { type: 'COMPARISON', left: { type: 'INDICATOR', indicator: 'TRIX', params: { period: 15 } }, op: 'CROSSES_ABOVE', right: { type: 'CONSTANT', value: 0 } },
  },
  {
    id: 'cmo-oversold', name: 'CMO Oversold', desc: 'CMO < -50', tip: 'Chande Momentum below -50 — strong downward pressure may reverse.', category: 'indicators', subCategory: 'Momentum',
    condition: { type: 'COMPARISON', left: { type: 'INDICATOR', indicator: 'CMO', params: { period: 14 } }, op: '<', right: { type: 'CONSTANT', value: -50 } },
  },
  {
    id: 'cmo-overbought', name: 'CMO Overbought', desc: 'CMO > 50', tip: 'Chande Momentum above 50 — strong upward pressure may reverse.', category: 'indicators', subCategory: 'Momentum',
    condition: { type: 'COMPARISON', left: { type: 'INDICATOR', indicator: 'CMO', params: { period: 14 } }, op: '>', right: { type: 'CONSTANT', value: 50 } },
  },
  {
    id: 'ultimate-osc-oversold', name: 'Ultimate Osc Oversold', desc: 'UO < 30', tip: 'Multi-timeframe oscillator showing oversold across multiple periods.', category: 'indicators', subCategory: 'Momentum',
    condition: { type: 'COMPARISON', left: { type: 'INDICATOR', indicator: 'ULTIMATE_OSC', params: { period: 14, fastPeriod: 7, slowPeriod: 28 } }, op: '<', right: { type: 'CONSTANT', value: 30 } },
  },
  {
    id: 'ultimate-osc-overbought', name: 'Ultimate Osc Overbought', desc: 'UO > 70', tip: 'Multi-timeframe oscillator showing overbought across multiple periods.', category: 'indicators', subCategory: 'Momentum',
    condition: { type: 'COMPARISON', left: { type: 'INDICATOR', indicator: 'ULTIMATE_OSC', params: { period: 14, fastPeriod: 7, slowPeriod: 28 } }, op: '>', right: { type: 'CONSTANT', value: 70 } },
  },
  {
    id: 'ppo-cross-up', name: 'PPO Cross Up', desc: 'PPO × above 0', tip: 'Percentage Price Oscillator crossing up — normalized MACD alternative.', category: 'indicators', subCategory: 'Momentum',
    condition: { type: 'COMPARISON', left: { type: 'INDICATOR', indicator: 'PPO', params: { period: 26, fastPeriod: 12, slowPeriod: 26 } }, op: 'CROSSES_ABOVE', right: { type: 'CONSTANT', value: 0 } },
  },
  {
    id: 'kst-cross-up', name: 'KST Cross Up', desc: 'KST × above 0', tip: 'Know Sure Thing crossing zero — combines multiple ROC periods.', category: 'indicators', subCategory: 'Momentum',
    condition: { type: 'COMPARISON', left: { type: 'INDICATOR', indicator: 'KST', params: { period: 14 } }, op: 'CROSSES_ABOVE', right: { type: 'CONSTANT', value: 0 } },
  },
  {
    id: 'coppock-turn-up', name: 'Coppock Turn Up', desc: 'Coppock × above 0', tip: 'Long-term momentum turning positive — originally for monthly signals.', category: 'indicators', subCategory: 'Momentum',
    condition: { type: 'COMPARISON', left: { type: 'INDICATOR', indicator: 'COPPOCK', params: { period: 10, fastPeriod: 11, slowPeriod: 14 } }, op: 'CROSSES_ABOVE', right: { type: 'CONSTANT', value: 0 } },
  },
  {
    id: 'dpo-positive', name: 'DPO Positive', desc: 'DPO > 0', tip: 'Detrended Price Oscillator above zero — shows cycles without trend.', category: 'indicators', subCategory: 'Momentum',
    condition: { type: 'COMPARISON', left: { type: 'INDICATOR', indicator: 'DPO', params: { period: 20 } }, op: '>', right: { type: 'CONSTANT', value: 0 } },
  },
  {
    id: 'macd-hist-positive', name: 'MACD Histogram +', desc: 'MACD Hist > 0', tip: 'MACD histogram positive — momentum accelerating in bullish direction.', category: 'indicators', subCategory: 'Momentum',
    condition: { type: 'COMPARISON', left: { type: 'INDICATOR', indicator: 'MACD_HISTOGRAM', params: { period: 26, fastPeriod: 12, slowPeriod: 26, signalPeriod: 9 } }, op: '>', right: { type: 'CONSTANT', value: 0 } },
  },
  // Indicators — Trend
  {
    id: 'price-above-sma', name: 'Above SMA', desc: 'Price > SMA(50)', tip: 'Price above 50-period moving average — confirms an active uptrend.', category: 'indicators', subCategory: 'Trend',
    condition: { type: 'COMPARISON', left: { type: 'PRICE', field: 'CURRENT' }, op: '>', right: { type: 'INDICATOR', indicator: 'SMA', params: { period: 50 } } },
  },
  {
    id: 'price-below-sma', name: 'Below SMA', desc: 'Price < SMA(50)', tip: 'Price below 50-period moving average — confirms an active downtrend.', category: 'indicators', subCategory: 'Trend',
    condition: { type: 'COMPARISON', left: { type: 'PRICE', field: 'CURRENT' }, op: '<', right: { type: 'INDICATOR', indicator: 'SMA', params: { period: 50 } } },
  },
  {
    id: 'ema-cross-up', name: 'EMA Cross Up', desc: 'EMA(9) × above EMA(21)', tip: 'Fast EMA crosses above slow EMA — classic golden cross signal.', category: 'indicators', subCategory: 'Trend',
    condition: { type: 'COMPARISON', left: { type: 'INDICATOR', indicator: 'EMA', params: { period: 9 } }, op: 'CROSSES_ABOVE', right: { type: 'INDICATOR', indicator: 'EMA', params: { period: 21 } } },
  },
  {
    id: 'adx-trending', name: 'ADX Trending', desc: 'ADX > 25 (strong trend)', tip: 'ADX above 25 means a strong trend — pair with direction indicators.', category: 'indicators', subCategory: 'Trend',
    condition: { type: 'COMPARISON', left: { type: 'INDICATOR', indicator: 'ADX', params: { period: 14 } }, op: '>', right: { type: 'CONSTANT', value: 25 } },
  },
  {
    id: 'psar-bullish', name: 'PSAR Bullish', desc: 'Price × above SAR', tip: 'Parabolic SAR flips below price — trend reversal to the upside.', category: 'indicators', subCategory: 'Trend',
    condition: { type: 'COMPARISON', left: { type: 'PRICE', field: 'CURRENT' }, op: 'CROSSES_ABOVE', right: { type: 'INDICATOR', indicator: 'PSAR', params: { period: 14, acceleration: 0.02, maxAcceleration: 0.2 } } },
  },
  {
    id: 'supertrend-bull', name: 'Supertrend Bull', desc: 'Price > Supertrend', tip: 'Price above Supertrend line — bullish trend with trailing stop.', category: 'indicators', subCategory: 'Trend',
    condition: { type: 'COMPARISON', left: { type: 'PRICE', field: 'CURRENT' }, op: '>', right: { type: 'INDICATOR', indicator: 'SUPERTREND', params: { period: 10, multiplier: 3 } } },
  },
  {
    id: 'above-vwap', name: 'Above VWAP', desc: 'Price > VWAP', tip: 'Price above volume-weighted average — institutional buyers in control.', category: 'indicators', subCategory: 'Trend',
    condition: { type: 'COMPARISON', left: { type: 'PRICE', field: 'CURRENT' }, op: '>', right: { type: 'INDICATOR', indicator: 'VWAP', params: { period: 20 } } },
  },
  {
    id: 'wma-cross-up', name: 'WMA Cross Up', desc: 'WMA(9) × above WMA(21)', tip: 'Weighted MA crossover — gives more weight to recent prices.', category: 'indicators', subCategory: 'Trend',
    condition: { type: 'COMPARISON', left: { type: 'INDICATOR', indicator: 'WMA', params: { period: 9 } }, op: 'CROSSES_ABOVE', right: { type: 'INDICATOR', indicator: 'WMA', params: { period: 21 } } },
  },
  {
    id: 'dema-cross-up', name: 'DEMA Cross Up', desc: 'DEMA(9) × above DEMA(21)', tip: 'Double EMA crossover — faster response than standard EMA.', category: 'indicators', subCategory: 'Trend',
    condition: { type: 'COMPARISON', left: { type: 'INDICATOR', indicator: 'DEMA', params: { period: 9 } }, op: 'CROSSES_ABOVE', right: { type: 'INDICATOR', indicator: 'DEMA', params: { period: 21 } } },
  },
  {
    id: 'tema-cross-up', name: 'TEMA Cross Up', desc: 'TEMA(9) × above TEMA(21)', tip: 'Triple EMA crossover — minimal lag for fast-moving markets.', category: 'indicators', subCategory: 'Trend',
    condition: { type: 'COMPARISON', left: { type: 'INDICATOR', indicator: 'TEMA', params: { period: 9 } }, op: 'CROSSES_ABOVE', right: { type: 'INDICATOR', indicator: 'TEMA', params: { period: 21 } } },
  },
  {
    id: 'hma-cross-up', name: 'HMA Cross Up', desc: 'HMA(9) × above HMA(21)', tip: 'Hull MA crossover — virtually eliminates lag while staying smooth.', category: 'indicators', subCategory: 'Trend',
    condition: { type: 'COMPARISON', left: { type: 'INDICATOR', indicator: 'HMA', params: { period: 9 } }, op: 'CROSSES_ABOVE', right: { type: 'INDICATOR', indicator: 'HMA', params: { period: 21 } } },
  },
  {
    id: 'lin-reg-up', name: 'LR Slope Positive', desc: 'LinReg slope > 0', tip: 'Linear regression slope positive — statistically confirmed uptrend.', category: 'indicators', subCategory: 'Trend',
    condition: { type: 'COMPARISON', left: { type: 'INDICATOR', indicator: 'LINEAR_REG_SLOPE', params: { period: 14 } }, op: '>', right: { type: 'CONSTANT', value: 0 } },
  },
  {
    id: 'aroon-bullish', name: 'Aroon Bullish', desc: 'Aroon Up > Aroon Down', tip: 'Aroon Up leads Down — price making new highs more recently.', category: 'indicators', subCategory: 'Trend',
    condition: { type: 'COMPARISON', left: { type: 'INDICATOR', indicator: 'AROON_UP', params: { period: 14 } }, op: '>', right: { type: 'INDICATOR', indicator: 'AROON_DOWN', params: { period: 14 } } },
  },
  {
    id: 'aroon-osc-bullish', name: 'Aroon Osc +', desc: 'Aroon Osc > 50', tip: 'Aroon Oscillator strongly positive — dominant uptrend with conviction.', category: 'indicators', subCategory: 'Trend',
    condition: { type: 'COMPARISON', left: { type: 'INDICATOR', indicator: 'AROON_OSC', params: { period: 14 } }, op: '>', right: { type: 'CONSTANT', value: 50 } },
  },
  {
    id: 'dmi-bullish', name: 'DMI Bullish', desc: '+DI > -DI', tip: 'Positive directional movement exceeds negative — buyers dominate.', category: 'indicators', subCategory: 'Trend',
    condition: { type: 'COMPARISON', left: { type: 'INDICATOR', indicator: 'DMI_PLUS', params: { period: 14 } }, op: '>', right: { type: 'INDICATOR', indicator: 'DMI_MINUS', params: { period: 14 } } },
  },
  {
    id: 'dmi-bearish', name: 'DMI Bearish', desc: '-DI > +DI', tip: 'Negative directional movement exceeds positive — sellers dominate.', category: 'indicators', subCategory: 'Trend',
    condition: { type: 'COMPARISON', left: { type: 'INDICATOR', indicator: 'DMI_MINUS', params: { period: 14 } }, op: '>', right: { type: 'INDICATOR', indicator: 'DMI_PLUS', params: { period: 14 } } },
  },
  // Indicators — Ichimoku
  {
    id: 'ichimoku-bull', name: 'Ichimoku Bullish', desc: 'Price > Kijun-sen', tip: 'Price above Kijun-sen baseline — medium-term bullish bias.', category: 'indicators', subCategory: 'Ichimoku',
    condition: { type: 'COMPARISON', left: { type: 'PRICE', field: 'CURRENT' }, op: '>', right: { type: 'INDICATOR', indicator: 'ICHIMOKU_KIJUN', params: { period: 26, conversionPeriod: 9, basePeriod: 26, spanPeriod: 52 } } },
  },
  {
    id: 'ichimoku-tk-cross', name: 'TK Cross Up', desc: 'Tenkan × above Kijun', tip: 'Tenkan crosses above Kijun — short-term momentum turning bullish.', category: 'indicators', subCategory: 'Ichimoku',
    condition: { type: 'COMPARISON', left: { type: 'INDICATOR', indicator: 'ICHIMOKU_TENKAN', params: { period: 26, conversionPeriod: 9, basePeriod: 26, spanPeriod: 52 } }, op: 'CROSSES_ABOVE', right: { type: 'INDICATOR', indicator: 'ICHIMOKU_KIJUN', params: { period: 26, conversionPeriod: 9, basePeriod: 26, spanPeriod: 52 } } },
  },
  {
    id: 'ichimoku-above-cloud', name: 'Above Kumo', desc: 'Price > Senkou A & B', tip: 'Price above both Senkou spans — strong bullish with cloud support.', category: 'indicators', subCategory: 'Ichimoku',
    condition: { type: 'AND', conditions: [
      { type: 'COMPARISON', left: { type: 'PRICE', field: 'CURRENT' }, op: '>', right: { type: 'INDICATOR', indicator: 'ICHIMOKU_SENKOU_A', params: { period: 26, conversionPeriod: 9, basePeriod: 26, spanPeriod: 52 } } },
      { type: 'COMPARISON', left: { type: 'PRICE', field: 'CURRENT' }, op: '>', right: { type: 'INDICATOR', indicator: 'ICHIMOKU_SENKOU_B', params: { period: 26, conversionPeriod: 9, basePeriod: 26, spanPeriod: 52 } } },
    ] },
  },
  // Indicators — Pivot Points
  {
    id: 'above-pivot', name: 'Above Pivot', desc: 'Price > PP', tip: 'Price above pivot point — intraday bullish bias.', category: 'indicators', subCategory: 'Pivot Points',
    condition: { type: 'COMPARISON', left: { type: 'PRICE', field: 'CURRENT' }, op: '>', right: { type: 'INDICATOR', indicator: 'PIVOT_PP', params: { period: 1 } } },
  },
  {
    id: 'near-support1', name: 'Near Support S1', desc: 'Price < S1', tip: 'Price below first support — potential bounce or breakdown zone.', category: 'indicators', subCategory: 'Pivot Points',
    condition: { type: 'COMPARISON', left: { type: 'PRICE', field: 'CURRENT' }, op: '<', right: { type: 'INDICATOR', indicator: 'PIVOT_S1', params: { period: 1 } } },
  },
  {
    id: 'near-resistance1', name: 'Near Resistance R1', desc: 'Price > R1', tip: 'Price above first resistance — potential breakout or rejection.', category: 'indicators', subCategory: 'Pivot Points',
    condition: { type: 'COMPARISON', left: { type: 'PRICE', field: 'CURRENT' }, op: '>', right: { type: 'INDICATOR', indicator: 'PIVOT_R1', params: { period: 1 } } },
  },
  // Indicators — Volatility
  {
    id: 'bb-upper-break', name: 'BB Upper Break', desc: 'Price > BB Upper', tip: 'Price breaks above Bollinger Band — breakout or reversal signal.', category: 'indicators', subCategory: 'Volatility',
    condition: { type: 'COMPARISON', left: { type: 'PRICE', field: 'CURRENT' }, op: '>', right: { type: 'INDICATOR', indicator: 'BB_UPPER', params: { period: 20, stdDev: 2 } } },
  },
  {
    id: 'bb-lower-break', name: 'BB Lower Break', desc: 'Price < BB Lower', tip: 'Price breaks below Bollinger Band — breakout or reversal signal.', category: 'indicators', subCategory: 'Volatility',
    condition: { type: 'COMPARISON', left: { type: 'PRICE', field: 'CURRENT' }, op: '<', right: { type: 'INDICATOR', indicator: 'BB_LOWER', params: { period: 20, stdDev: 2 } } },
  },
  {
    id: 'keltner-upper-break', name: 'Keltner Upper Break', desc: 'Price > Keltner Upper', tip: 'Price above Keltner Channel — strong momentum breakout upside.', category: 'indicators', subCategory: 'Volatility',
    condition: { type: 'COMPARISON', left: { type: 'PRICE', field: 'CURRENT' }, op: '>', right: { type: 'INDICATOR', indicator: 'KELTNER_UPPER', params: { period: 20, multiplier: 1.5 } } },
  },
  {
    id: 'keltner-lower-break', name: 'Keltner Lower Break', desc: 'Price < Keltner Lower', tip: 'Price below Keltner Channel — strong momentum breakout downside.', category: 'indicators', subCategory: 'Volatility',
    condition: { type: 'COMPARISON', left: { type: 'PRICE', field: 'CURRENT' }, op: '<', right: { type: 'INDICATOR', indicator: 'KELTNER_LOWER', params: { period: 20, multiplier: 1.5 } } },
  },
  {
    id: 'donchian-upper-break', name: 'Donchian Upper Break', desc: 'Price > Donchian High', tip: 'Price at highest high of period — turtle trading breakout.', category: 'indicators', subCategory: 'Volatility',
    condition: { type: 'COMPARISON', left: { type: 'PRICE', field: 'CURRENT' }, op: '>', right: { type: 'INDICATOR', indicator: 'DONCHIAN_UPPER', params: { period: 20 } } },
  },
  {
    id: 'donchian-lower-break', name: 'Donchian Lower Break', desc: 'Price < Donchian Low', tip: 'Price at lowest low of period — downside breakout signal.', category: 'indicators', subCategory: 'Volatility',
    condition: { type: 'COMPARISON', left: { type: 'PRICE', field: 'CURRENT' }, op: '<', right: { type: 'INDICATOR', indicator: 'DONCHIAN_LOWER', params: { period: 20 } } },
  },
  {
    id: 'chandelier-exit-long', name: 'Chandelier Exit Long', desc: 'Price < Chandelier Exit', tip: 'Price drops below volatility trailing stop — exit long positions.', category: 'indicators', subCategory: 'Volatility',
    condition: { type: 'COMPARISON', left: { type: 'PRICE', field: 'CURRENT' }, op: '<', right: { type: 'INDICATOR', indicator: 'CHANDELIER_EXIT_LONG', params: { period: 22, multiplier: 3 } } },
  },
  {
    id: 'atr-high', name: 'High ATR', desc: 'ATR > value (volatile)', tip: 'Average True Range elevated — market is volatile, adjust size.', category: 'indicators', subCategory: 'Volatility',
    condition: { type: 'COMPARISON', left: { type: 'INDICATOR', indicator: 'ATR', params: { period: 14 } }, op: '>', right: { type: 'CONSTANT', value: 1 } },
  },
  {
    id: 'range-breakout', name: 'Range Breakout Up', desc: 'Price > Range High', tip: 'Price breaks above recent range high — potential new trend.', category: 'indicators', subCategory: 'Volatility',
    condition: { type: 'COMPARISON', left: { type: 'PRICE', field: 'CURRENT' }, op: '>', right: { type: 'INDICATOR', indicator: 'RANGE_HIGH', params: { period: 20 } } },
  },
  {
    id: 'highest-high-break', name: 'New Highest High', desc: 'Price > Highest High', tip: 'New highest high — strong upward momentum breakout signal.', category: 'indicators', subCategory: 'Volatility',
    condition: { type: 'COMPARISON', left: { type: 'PRICE', field: 'CURRENT' }, op: '>', right: { type: 'INDICATOR', indicator: 'HIGHEST_HIGH', params: { period: 20 } } },
  },
  {
    id: 'lowest-low-break', name: 'New Lowest Low', desc: 'Price < Lowest Low', tip: 'New lowest low — strong downward momentum breakdown signal.', category: 'indicators', subCategory: 'Volatility',
    condition: { type: 'COMPARISON', left: { type: 'PRICE', field: 'CURRENT' }, op: '<', right: { type: 'INDICATOR', indicator: 'LOWEST_LOW', params: { period: 20 } } },
  },
  // Indicators — Volume
  {
    id: 'obv-rising', name: 'OBV Rising', desc: 'OBV > 0', tip: 'On Balance Volume positive — accumulation supports price direction.', category: 'indicators', subCategory: 'Volume',
    condition: { type: 'COMPARISON', left: { type: 'INDICATOR', indicator: 'OBV', params: { period: 14 } }, op: '>', right: { type: 'CONSTANT', value: 0 } },
  },
  {
    id: 'mfi-oversold', name: 'MFI Oversold', desc: 'MFI < 20', tip: 'Money Flow Index below 20 — selling exhausted with volume confirm.', category: 'indicators', subCategory: 'Volume',
    condition: { type: 'COMPARISON', left: { type: 'INDICATOR', indicator: 'MFI', params: { period: 14 } }, op: '<', right: { type: 'CONSTANT', value: 20 } },
  },
  {
    id: 'mfi-overbought', name: 'MFI Overbought', desc: 'MFI > 80', tip: 'Money Flow Index above 80 — buying exhausted with volume confirm.', category: 'indicators', subCategory: 'Volume',
    condition: { type: 'COMPARISON', left: { type: 'INDICATOR', indicator: 'MFI', params: { period: 14 } }, op: '>', right: { type: 'CONSTANT', value: 80 } },
  },
  {
    id: 'cmf-positive', name: 'CMF Positive', desc: 'CMF > 0 (buying)', tip: 'Chaikin Money Flow positive — sustained buying pressure.', category: 'indicators', subCategory: 'Volume',
    condition: { type: 'COMPARISON', left: { type: 'INDICATOR', indicator: 'CMF', params: { period: 20 } }, op: '>', right: { type: 'CONSTANT', value: 0 } },
  },
  {
    id: 'force-index-positive', name: 'Force Index +', desc: 'Force > 0 (bull pressure)', tip: 'Force Index positive — price moves backed by strong volume.', category: 'indicators', subCategory: 'Volume',
    condition: { type: 'COMPARISON', left: { type: 'INDICATOR', indicator: 'FORCE_INDEX', params: { period: 13 } }, op: '>', right: { type: 'CONSTANT', value: 0 } },
  },
  {
    id: 'adl-rising', name: 'ADL Rising', desc: 'ADL > 0', tip: 'Accumulation/Distribution rising — smart money is accumulating.', category: 'indicators', subCategory: 'Volume',
    condition: { type: 'COMPARISON', left: { type: 'INDICATOR', indicator: 'ADL', params: { period: 14 } }, op: '>', right: { type: 'CONSTANT', value: 0 } },
  },
  // Price
  {
    id: 'price-above-value', name: 'Price Above', desc: 'Price > value', tip: 'Basic price threshold — filter trades above a key level.', category: 'price',
    condition: { type: 'COMPARISON', left: { type: 'PRICE', field: 'CURRENT' }, op: '>', right: { type: 'CONSTANT', value: 100 } },
  },
  {
    id: 'price-below-value', name: 'Price Below', desc: 'Price < value', tip: 'Basic price threshold — filter trades below a key level.', category: 'price',
    condition: { type: 'COMPARISON', left: { type: 'PRICE', field: 'CURRENT' }, op: '<', right: { type: 'CONSTANT', value: 100 } },
  },
  {
    id: 'high-above-prev', name: 'New High', desc: 'High > Prev Tick High', tip: 'Current high exceeds previous tick high — immediate upward pressure.', category: 'price',
    condition: { type: 'COMPARISON', left: { type: 'PRICE', field: 'HIGH' }, op: '>', right: { type: 'PRICE', field: 'PREV_TICK_HIGH' } },
  },
  {
    id: 'low-below-prev', name: 'New Low', desc: 'Low < Prev Tick Low', tip: 'Current low below previous tick low — immediate downward pressure.', category: 'price',
    condition: { type: 'COMPARISON', left: { type: 'PRICE', field: 'LOW' }, op: '<', right: { type: 'PRICE', field: 'PREV_TICK_LOW' } },
  },
  {
    id: 'volume-spike', name: 'Volume Spike', desc: 'Volume > 2x avg', tip: 'Volume exceeds its moving average — unusual activity, potential move.', category: 'price',
    condition: { type: 'COMPARISON', left: { type: 'PRICE', field: 'VOLUME' }, op: '>', right: { type: 'INDICATOR', indicator: 'VOLUME_SMA', params: { period: 20 } } },
  },
  {
    id: 'volume-drop', name: 'Low Volume', desc: 'Volume < avg', tip: 'Volume below average — low participation, often precedes breakout.', category: 'price',
    condition: { type: 'COMPARISON', left: { type: 'PRICE', field: 'VOLUME' }, op: '<', right: { type: 'INDICATOR', indicator: 'VOLUME_SMA', params: { period: 20 } } },
  },
  // Candle Analysis
  {
    id: 'spike-filter', name: 'No Spike', desc: 'Spike Ratio < 2.5', tip: 'Filters out entries during abnormally large candles (spikes, news events, jumps). Current candle range must be less than 2.5x the average.', category: 'indicators', subCategory: 'Candle Analysis',
    condition: { type: 'COMPARISON', left: { type: 'INDICATOR', indicator: 'SPIKE_RATIO', params: { period: 20 } }, op: '<', right: { type: 'CONSTANT', value: 2.5 } },
  },
  {
    id: 'spike-detected', name: 'Spike Detected', desc: 'Spike Ratio > 3', tip: 'Detects abnormally large candles — useful for breakout strategies that want to catch big moves.', category: 'indicators', subCategory: 'Candle Analysis',
    condition: { type: 'COMPARISON', left: { type: 'INDICATOR', indicator: 'SPIKE_RATIO', params: { period: 20 } }, op: '>', right: { type: 'CONSTANT', value: 3 } },
  },
  {
    id: 'active-market', name: 'Active Market', desc: 'Candle Range > ATR', tip: 'Current candle is larger than the average true range — market is moving, not flat.', category: 'indicators', subCategory: 'Candle Analysis',
    condition: { type: 'COMPARISON', left: { type: 'INDICATOR', indicator: 'CANDLE_RANGE', params: { period: 1 } }, op: '>', right: { type: 'INDICATOR', indicator: 'ATR', params: { period: 14 } } },
  },
  {
    id: 'strong-candle', name: 'Strong Candle', desc: 'Body > 5 points', tip: 'Current candle has strong directional movement — not a doji or indecision candle.', category: 'indicators', subCategory: 'Candle Analysis',
    condition: { type: 'COMPARISON', left: { type: 'INDICATOR', indicator: 'CANDLE_BODY', params: { period: 1 } }, op: '>', right: { type: 'CONSTANT', value: 5 } },
  },
  {
    id: 'prev-spike-cleared', name: 'Post-Spike Calm', desc: 'Prev Spike Ratio < 2', tip: 'The previous candle was NOT a spike — market has stabilized after any recent spikes. Good for entering after volatility settles.', category: 'indicators', subCategory: 'Candle Analysis',
    condition: { type: 'COMPARISON', left: { type: 'INDICATOR', indicator: 'SPIKE_RATIO', params: { period: 20 }, offset: 1 }, op: '<', right: { type: 'CONSTANT', value: 2 } },
  },
  // Session
  {
    id: 'london-session', name: 'London Session', desc: 'Hour 8-16 UTC', tip: 'Trade during London hours (8-16 UTC) — peak forex liquidity.', category: 'session',
    condition: { type: 'AND', conditions: [
      { type: 'COMPARISON', left: { type: 'TIME', field: 'HOUR' }, op: '>=', right: { type: 'CONSTANT', value: 8 } },
      { type: 'COMPARISON', left: { type: 'TIME', field: 'HOUR' }, op: '<=', right: { type: 'CONSTANT', value: 16 } },
    ] },
  },
  {
    id: 'ny-session', name: 'New York Session', desc: 'Hour 13-21 UTC', tip: 'Trade during New York hours (13-21 UTC) — high volatility overlap.', category: 'session',
    condition: { type: 'AND', conditions: [
      { type: 'COMPARISON', left: { type: 'TIME', field: 'HOUR' }, op: '>=', right: { type: 'CONSTANT', value: 13 } },
      { type: 'COMPARISON', left: { type: 'TIME', field: 'HOUR' }, op: '<=', right: { type: 'CONSTANT', value: 21 } },
    ] },
  },
  // Position
  {
    id: 'no-positions', name: 'No Positions', desc: 'Position count = 0', tip: 'No open positions — use as guard to prevent duplicate entries.', category: 'position',
    condition: { type: 'COMPARISON', left: { type: 'POSITION', field: 'COUNT' }, op: '==', right: { type: 'CONSTANT', value: 0 } },
  },
  {
    id: 'profit-target', name: 'Profit Target', desc: 'Floating P&L ≥ 2%', tip: 'Floating profit at or above 2% — take-profit exit condition.', category: 'position',
    condition: { type: 'COMPARISON', left: { type: 'POSITION', field: 'FLOATING_PNL_PCT' }, op: '>=', right: { type: 'CONSTANT', value: 2 } },
  },
  {
    id: 'max-basket', name: 'Max Basket Vol', desc: 'Basket volume < 0.5', tip: 'Total open volume under 0.5 lots — limits overall exposure.', category: 'position',
    condition: { type: 'COMPARISON', left: { type: 'POSITION', field: 'BASKET_VOLUME' }, op: '<', right: { type: 'CONSTANT', value: 0.5 } },
  },
  // Account
  {
    id: 'equity-above', name: 'Equity Above', desc: 'Equity > value', tip: 'Equity exceeds threshold — ensures account health before trades.', category: 'account',
    condition: { type: 'COMPARISON', left: { type: 'ACCOUNT', field: 'EQUITY' }, op: '>', right: { type: 'CONSTANT', value: 500 } },
  },
  {
    id: 'equity-drawdown', name: 'Equity < Capital', desc: 'Equity below assigned', tip: 'Equity below assigned capital — account is in drawdown.', category: 'account',
    condition: { type: 'COMPARISON', left: { type: 'ACCOUNT', field: 'EQUITY' }, op: '<', right: { type: 'ACCOUNT', field: 'ASSIGNED_CAPITAL' } },
  },
  {
    id: 'balance-check', name: 'Balance Above', desc: 'Balance > value', tip: 'Balance above threshold — basic account health check.', category: 'account',
    condition: { type: 'COMPARISON', left: { type: 'ACCOUNT', field: 'BALANCE' }, op: '>', right: { type: 'CONSTANT', value: 1000 } },
  },
];

const PRESET_CATEGORIES: { id: PresetCategory; label: string; icon: ReactNode }[] = [
  { id: 'indicators', label: 'Indicators', icon: <Activity size={12} /> },
  { id: 'price', label: 'Price', icon: <BarChart3 size={12} /> },
  { id: 'session', label: 'Session', icon: <Clock size={12} /> },
  { id: 'position', label: 'Position', icon: <Target size={12} /> },
  { id: 'account', label: 'Account', icon: <DollarSign size={12} /> },
];

// ─── Extended Reducer (adds APPLY_PRESET action) ─────────

type FormAction =
  | DraftAction
  | { type: 'REPLACE_CONDITION'; path: string; condition: Condition }
  | { type: 'ADD_TO_GROUP'; groupPath: string; condition: Condition }
  | { type: 'WRAP_AND_ADD'; path: string; condition: Condition }
  | { type: 'ADD_SUB_GROUP'; groupPath: string; condition: Condition }
  | { type: 'CLEAR_CONDITION'; path: string };

function isAlwaysTrue(c: Condition): boolean {
  return c.type === 'COMPARISON' &&
    c.left.type === 'CONSTANT' && c.left.value === 1 &&
    c.right.type === 'CONSTANT' && c.right.value === 1;
}

const TRIVIAL_CONDITION: ComparisonCondition = {
  type: 'COMPARISON',
  left: { type: 'CONSTANT', value: 1 },
  op: '==',
  right: { type: 'CONSTANT', value: 1 },
};


function formReducer(state: ConditionEngineRules | null, action: FormAction): ConditionEngineRules | null {
  if (!state) return action.type === 'INIT' ? draftReducer(state, action as DraftAction) : state;

  switch (action.type) {
    case 'REPLACE_CONDITION':
      return setByPathImmutable(state, action.path, structuredClone(action.condition));

    case 'ADD_TO_GROUP': {
      const group = getByPath(state, action.groupPath) as ConditionGroup;
      // Flatten: if the new condition is a group, spread its children
      const toAdd = action.condition.type === 'AND' || action.condition.type === 'OR'
        ? action.condition.conditions : [action.condition];
      const newConds = [...group.conditions, ...toAdd.map((c) => structuredClone(c))];
      return setByPathImmutable(state, `${action.groupPath}.conditions`, newConds);
    }

    case 'WRAP_AND_ADD': {
      const current = getByPath(state, action.path) as Condition;
      // Flatten: if the new condition is a group, spread its children
      const toAdd = action.condition.type === 'AND' || action.condition.type === 'OR'
        ? action.condition.conditions : [action.condition];
      const group: ConditionGroup = {
        type: 'AND',
        conditions: [structuredClone(current), ...toAdd.map((c) => structuredClone(c))],
      };
      return setByPathImmutable(state, action.path, group);
    }

    case 'CLEAR_CONDITION':
      return setByPathImmutable(state, action.path, structuredClone(TRIVIAL_CONDITION));

    // Add a sub-group with the selected condition inside
    case 'ADD_SUB_GROUP': {
      const group = getByPath(state, action.groupPath) as ConditionGroup;
      const toAdd = action.condition.type === 'AND' || action.condition.type === 'OR'
        ? action.condition.conditions : [action.condition];
      const newGroup: ConditionGroup = { type: 'AND', conditions: toAdd.map((c) => structuredClone(c)) };
      const newConds = [...group.conditions, newGroup];
      return setByPathImmutable(state, `${action.groupPath}.conditions`, newConds);
    }

    // Override TOGGLE_GROUP_TYPE: only toggle the exact target, not children
    case 'TOGGLE_GROUP_TYPE': {
      const group = getByPath(state, action.path) as ConditionGroup;
      return setByPathImmutable(state, `${action.path}.type`, group.type === 'AND' ? 'OR' : 'AND');
    }

    // Override CHANGE_DIRECTION: use trivial conditions for SIGNAL instead of defaults
    case 'CHANGE_DIRECTION': {
      const rule = state.entry.rules[action.ruleIndex]!;
      const updated = { ...rule, direction: action.direction };
      if (action.direction === 'SIGNAL') {
        if (!updated.buyCondition) {
          updated.buyCondition = structuredClone(TRIVIAL_CONDITION);
        }
        if (!updated.sellCondition) {
          updated.sellCondition = structuredClone(TRIVIAL_CONDITION);
        }
      }
      const rules = state.entry.rules.map((r, i) => i === action.ruleIndex ? updated : r);
      return { ...state, entry: { ...state.entry, rules } };
    }

    default:
      return draftReducer(state, action as DraftAction);
  }
}

// ─── Styled Select (supports optgroups) ──────────────────

function StyledSelect({
  value,
  onChange,
  options,
  groups,
  className = '',
}: {
  value: string;
  onChange: (v: string) => void;
  options?: { value: string; label: string }[];
  groups?: Record<string, { value: string; label: string }[]>;
  className?: string;
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={`bg-surface border border-glass-border rounded-lg px-3 py-2 text-sm font-mono text-ink outline-none transition-all duration-200 focus:border-orange focus:shadow-[0_0_8px_rgba(249,115,22,0.15)] cursor-pointer appearance-none ${className}`}
    >
      {options?.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      {groups && Object.entries(groups).map(([group, items]) => (
        <optgroup key={group} label={group}>
          {items.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </optgroup>
      ))}
    </select>
  );
}

// ─── Combined ValueRef options (single dropdown) ────────

const COMBINED_REF_OPTIONS: Record<string, { value: string; label: string }[]> = {
  Price: PRICE_OPTIONS.map((o) => ({ value: `PRICE:${o.value}`, label: o.label })),
  ...Object.fromEntries(
    Object.entries(INDICATOR_CATEGORIES).map(([cat, items]) => [
      cat,
      items.map((o) => ({ value: `INDICATOR:${o.value}`, label: o.label })),
    ]),
  ),
  Position: POSITION_OPTIONS.map((o) => ({ value: `POSITION:${o.value}`, label: o.label })),
  Account: ACCOUNT_OPTIONS.map((o) => ({ value: `ACCOUNT:${o.value}`, label: o.label })),
  Time: TIME_OPTIONS.map((o) => ({ value: `TIME:${o.value}`, label: o.label })),
  Value: [{ value: 'CONSTANT', label: 'Number' }],
};

function encodeRef(vr: ValueRef): string {
  if (vr.type === 'CONSTANT') return 'CONSTANT';
  if (vr.type === 'INDICATOR') return `INDICATOR:${vr.indicator ?? 'RSI'}`;
  return `${vr.type}:${vr.field ?? ''}`;
}

function decodeRefChange(encoded: string, path: string, dispatch: React.Dispatch<FormAction>) {
  if (encoded === 'CONSTANT') {
    dispatch({ type: 'CHANGE_VALUE_REF_TYPE', path, newType: 'CONSTANT' });
    return;
  }
  const [type, value] = encoded.split(':') as [string, string];
  if (type === 'INDICATOR') {
    // Switch to indicator or change indicator
    dispatch({ type: 'SET_PATH', path, value: {
      type: 'INDICATOR',
      indicator: value,
      params: {
        period: 14,
        ...(value.startsWith('BB') ? { stdDev: 2 } : {}),
        ...(value === 'PSAR' ? { acceleration: 0.02, maxAcceleration: 0.2 } : {}),
        ...(value === 'SUPERTREND' || value.startsWith('KELTNER') || value.startsWith('CHANDELIER') ? { multiplier: value.startsWith('KELTNER') ? 1.5 : 3 } : {}),
        ...((value === 'MACD' || value === 'MACD_SIGNAL' || value === 'MACD_HISTOGRAM') ? { fastPeriod: 12, slowPeriod: 26, signalPeriod: 9 } : {}),
        ...((value === 'PPO' || value === 'AWESOME_OSC') ? { fastPeriod: value === 'AWESOME_OSC' ? 5 : 12, slowPeriod: value === 'AWESOME_OSC' ? 34 : 26 } : {}),
        ...(value === 'ULTIMATE_OSC' ? { fastPeriod: 7, slowPeriod: 28 } : {}),
        ...(value.startsWith('ICHIMOKU') ? { conversionPeriod: 9, basePeriod: 26, spanPeriod: 52 } : {}),
        ...((value === 'STOCH_K' || value === 'STOCH_D') ? { signalPeriod: 3 } : {}),
      },
    } });
  } else {
    dispatch({ type: 'SET_PATH', path, value: { type, field: value } });
  }
}

// ─── Enhanced ValueRef Editor ────────────────────────────

function EnhancedValueRef({
  vr,
  path,
  dispatch,
}: {
  vr: ValueRef;
  path: string;
  dispatch: React.Dispatch<FormAction>;
}) {
  if (!vr) return null;

  return (
    <div className="flex items-center gap-1 flex-wrap">
      {/* Single combined dropdown */}
      <StyledSelect
        value={encodeRef(vr)}
        onChange={(v) => decodeRefChange(v, path, dispatch)}
        groups={COMBINED_REF_OPTIONS}
        className="!w-[140px] !flex-none"
      />

      {/* Number input for CONSTANT */}
      {vr.type === 'CONSTANT' && (
        <EdInput
          value={vr.value ?? 0}
          onChange={(v) => dispatch({ type: 'SET_PATH_NUM', path: `${path}.value`, value: v })}
          step="any"
          small
        />
      )}

      {/* Price offset (candle-based fields only — not CURRENT or PREV_TICK_*) */}
      {vr.type === 'PRICE' && vr.field && !['CURRENT', 'PREV_TICK_HIGH', 'PREV_TICK_LOW'].includes(vr.field) && (
        <>
          <span className="text-[9px] text-ink-4 font-semibold ml-1">offset</span>
          <EdInput
            value={vr.offset ?? 0}
            onChange={(v) => dispatch({ type: 'SET_PATH_NUM', path: `${path}.offset`, value: v })}
            min="0" max="200" step="1" small className="!w-[35px]"
          />
          {(vr.offset ?? 0) > 200 && (
            <span className="text-[9px] text-red font-semibold">max 200</span>
          )}
        </>
      )}

      {/* Indicator params */}
      {vr.type === 'INDICATOR' && (
        <>
          <EdInput
            value={vr.params?.period ?? 14}
            onChange={(v) => dispatch({ type: 'SET_PATH_NUM', path: `${path}.params.period`, value: v })}
            min="1" step="1" small className="!w-[45px]"
          />

          {vr.indicator?.startsWith('BB') && (
            <>
              <span className="text-[9px] text-ink-4 font-semibold">&sigma;</span>
              <EdInput
                value={vr.params?.stdDev ?? 2}
                onChange={(v) => dispatch({ type: 'SET_PATH_NUM', path: `${path}.params.stdDev`, value: v })}
                step="0.1" small className="!w-[40px]"
              />
            </>
          )}

          {(vr.indicator === 'MACD' || vr.indicator === 'MACD_SIGNAL' || vr.indicator === 'MACD_HISTOGRAM') && (
            <>
              <span className="text-[9px] text-ink-4 font-semibold">fast</span>
              <EdInput
                value={vr.params?.fastPeriod ?? 12}
                onChange={(v) => dispatch({ type: 'SET_PATH_NUM', path: `${path}.params.fastPeriod`, value: v })}
                min="1" step="1" small className="!w-[35px]"
              />
              <span className="text-[9px] text-ink-4 font-semibold">slow</span>
              <EdInput
                value={vr.params?.slowPeriod ?? 26}
                onChange={(v) => dispatch({ type: 'SET_PATH_NUM', path: `${path}.params.slowPeriod`, value: v })}
                min="1" step="1" small className="!w-[35px]"
              />
              <span className="text-[9px] text-ink-4 font-semibold">sig</span>
              <EdInput
                value={vr.params?.signalPeriod ?? 9}
                onChange={(v) => dispatch({ type: 'SET_PATH_NUM', path: `${path}.params.signalPeriod`, value: v })}
                min="1" step="1" small className="!w-[35px]"
              />
            </>
          )}

          {(vr.indicator === 'STOCH_K' || vr.indicator === 'STOCH_D') && (
            <>
              <span className="text-[9px] text-ink-4 font-semibold">signal</span>
              <EdInput
                value={vr.params?.signalPeriod ?? 3}
                onChange={(v) => dispatch({ type: 'SET_PATH_NUM', path: `${path}.params.signalPeriod`, value: v })}
                min="1" step="1" small className="!w-[35px]"
              />
            </>
          )}

          {vr.indicator === 'PSAR' && (
            <>
              <span className="text-[9px] text-ink-4 font-semibold">step</span>
              <EdInput
                value={vr.params?.acceleration ?? 0.02}
                onChange={(v) => dispatch({ type: 'SET_PATH_NUM', path: `${path}.params.acceleration`, value: v })}
                step="0.01" small className="!w-[45px]"
              />
              <span className="text-[9px] text-ink-4 font-semibold">max</span>
              <EdInput
                value={vr.params?.maxAcceleration ?? 0.2}
                onChange={(v) => dispatch({ type: 'SET_PATH_NUM', path: `${path}.params.maxAcceleration`, value: v })}
                step="0.01" small className="!w-[45px]"
              />
            </>
          )}

          {(vr.indicator === 'SUPERTREND' || vr.indicator?.startsWith('KELTNER') || vr.indicator?.startsWith('CHANDELIER')) && (
            <>
              <span className="text-[9px] text-ink-4 font-semibold">mult</span>
              <EdInput
                value={vr.params?.multiplier ?? (vr.indicator?.startsWith('KELTNER') ? 1.5 : 3)}
                onChange={(v) => dispatch({ type: 'SET_PATH_NUM', path: `${path}.params.multiplier`, value: v })}
                step="0.1" small className="!w-[40px]"
              />
            </>
          )}

          {(vr.indicator === 'PPO' || vr.indicator === 'AWESOME_OSC') && (
            <>
              <span className="text-[9px] text-ink-4 font-semibold">fast</span>
              <EdInput
                value={vr.params?.fastPeriod ?? (vr.indicator === 'AWESOME_OSC' ? 5 : 12)}
                onChange={(v) => dispatch({ type: 'SET_PATH_NUM', path: `${path}.params.fastPeriod`, value: v })}
                min="1" step="1" small className="!w-[35px]"
              />
              <span className="text-[9px] text-ink-4 font-semibold">slow</span>
              <EdInput
                value={vr.params?.slowPeriod ?? (vr.indicator === 'AWESOME_OSC' ? 34 : 26)}
                onChange={(v) => dispatch({ type: 'SET_PATH_NUM', path: `${path}.params.slowPeriod`, value: v })}
                min="1" step="1" small className="!w-[35px]"
              />
            </>
          )}

          {vr.indicator === 'ULTIMATE_OSC' && (
            <>
              <span className="text-[9px] text-ink-4 font-semibold">fast</span>
              <EdInput
                value={vr.params?.fastPeriod ?? 7}
                onChange={(v) => dispatch({ type: 'SET_PATH_NUM', path: `${path}.params.fastPeriod`, value: v })}
                min="1" step="1" small className="!w-[35px]"
              />
              <span className="text-[9px] text-ink-4 font-semibold">slow</span>
              <EdInput
                value={vr.params?.slowPeriod ?? 28}
                onChange={(v) => dispatch({ type: 'SET_PATH_NUM', path: `${path}.params.slowPeriod`, value: v })}
                min="1" step="1" small className="!w-[35px]"
              />
            </>
          )}

          {vr.indicator?.startsWith('ICHIMOKU') && (
            <>
              <span className="text-[9px] text-ink-4 font-semibold">conv</span>
              <EdInput
                value={vr.params?.conversionPeriod ?? 9}
                onChange={(v) => dispatch({ type: 'SET_PATH_NUM', path: `${path}.params.conversionPeriod`, value: v })}
                min="1" step="1" small className="!w-[35px]"
              />
              <span className="text-[9px] text-ink-4 font-semibold">base</span>
              <EdInput
                value={vr.params?.basePeriod ?? 26}
                onChange={(v) => dispatch({ type: 'SET_PATH_NUM', path: `${path}.params.basePeriod`, value: v })}
                min="1" step="1" small className="!w-[35px]"
              />
              <span className="text-[9px] text-ink-4 font-semibold">span</span>
              <EdInput
                value={vr.params?.spanPeriod ?? 52}
                onChange={(v) => dispatch({ type: 'SET_PATH_NUM', path: `${path}.params.spanPeriod`, value: v })}
                min="1" step="1" small className="!w-[35px]"
              />
            </>
          )}

          <span className="text-[9px] text-ink-4 font-semibold ml-1">offset</span>
          <EdInput
            value={vr.offset ?? 0}
            onChange={(v) => dispatch({ type: 'SET_PATH_NUM', path: `${path}.offset`, value: v })}
            min="0" max="200" step="1" small className="!w-[35px]"
          />
          {(vr.offset ?? 0) > 200 && (
            <span className="text-[9px] text-red font-semibold">max 200</span>
          )}
        </>
      )}
    </div>
  );
}

// ─── Condition Row (single comparison) ──────────────────

function ConditionRow({
  cond,
  path,
  dispatch,
  onRemove,
}: {
  cond: ComparisonCondition;
  path: string;
  dispatch: React.Dispatch<FormAction>;
  onRemove?: () => void;
}) {
  const opOptions = OPS.map((o) => ({ value: o, label: OP_LABELS[o] ?? o }));

  return (
    <div className="flex items-center gap-1 flex-wrap rounded-lg p-2.5 border border-glass-border bg-glass">
      <EnhancedValueRef vr={cond.left} path={`${path}.left`} dispatch={dispatch} />

      <StyledSelect
        value={cond.op}
        onChange={(v) => dispatch({ type: 'SET_PATH', path: `${path}.op`, value: v })}
        options={opOptions}
        className="!w-[110px] !flex-none"
      />

      <EnhancedValueRef vr={cond.right} path={`${path}.right`} dispatch={dispatch} />

      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          className="w-[22px] h-[22px] rounded-md border border-glass-border text-ink-4 hover:border-red hover:text-red flex items-center justify-center cursor-pointer transition-colors flex-shrink-0 ml-auto"
          title="Remove condition"
        >
          <X size={11} />
        </button>
      )}
    </div>
  );
}

// ─── Enhanced Condition Editor ───────────────────────────

function EnhancedConditionEditor({
  condition,
  path,
  dispatch,
  onOpenModal,
}: {
  condition: Condition | undefined;
  path: string;
  dispatch: React.Dispatch<FormAction>;
  onOpenModal: (mode: ModalMode) => void;
}) {
  if (!condition) return <em className="text-ink-4 text-[10px]">No condition</em>;

  // Trivial 1==1 — show "Set Condition" prompt
  if (condition.type === 'COMPARISON' && isAlwaysTrue(condition)) {
    return (
      <div className="rounded-lg p-2.5 border border-dashed border-ink-4/30 bg-glass">
        <button
          type="button"
          onClick={() => onOpenModal({ type: 'replace', path })}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-dashed border-orange/30 text-ink-4 hover:border-orange hover:text-orange text-[10px] font-bold cursor-pointer transition-colors"
        >
          <Plus size={10} />
          Set Condition
        </button>
      </div>
    );
  }

  // Single comparison — show row + "Add Condition" button
  if (condition.type === 'COMPARISON') {
    return (
      <div className="space-y-1.5">
        <ConditionRow
          cond={condition}
          path={path}
          dispatch={dispatch}
          onRemove={() => dispatch({ type: 'CLEAR_CONDITION', path })}
        />
        <button
          type="button"
          onClick={() => onOpenModal({ type: 'wrapAndAdd', path })}
          className="flex items-center gap-1 px-2.5 py-1 rounded-md text-ink-4 hover:text-orange text-[9px] font-bold cursor-pointer transition-colors"
        >
          <Plus size={9} />
          Add Condition
        </button>
      </div>
    );
  }

  // Empty group — show "Set Condition" prompt (same as trivial 1==1)
  if ((condition.type === 'AND' || condition.type === 'OR') && condition.conditions.length === 0) {
    return (
      <div className="rounded-lg p-2.5 border border-dashed border-ink-4/30 bg-glass">
        <button
          type="button"
          onClick={() => onOpenModal({ type: 'replace', path })}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-dashed border-orange/30 text-ink-4 hover:border-orange hover:text-orange text-[10px] font-bold cursor-pointer transition-colors"
        >
          <Plus size={10} />
          Set Condition
        </button>
      </div>
    );
  }

  // Group (AND/OR) — colored border, single toggle at top, supports nested groups
  if (condition.type === 'AND' || condition.type === 'OR') {
    const removeChild = (i: number) => {
      if (condition.conditions.length <= 1) {
        dispatch({ type: 'UNWRAP_GROUP', path });
      } else {
        dispatch({ type: 'REMOVE_CONDITION', groupPath: path, index: i });
      }
    };

    return (
      <div className={`border-l-2 ${condition.type === 'AND' ? 'border-blue/40' : 'border-orange/40'} pl-3`}>
        {/* Logic toggle — only show when 2+ conditions */}
        {condition.conditions.length > 1 && (
          <div className="flex items-center gap-2 mb-2">
            <button
              type="button"
              onClick={() => dispatch({ type: 'TOGGLE_GROUP_TYPE', path })}
              className={`text-[10px] font-extrabold px-3 py-1 rounded-full cursor-pointer transition-colors border ${
                condition.type === 'AND'
                  ? 'border-blue/30 bg-blue/10 text-blue hover:bg-blue/20'
                  : 'border-orange/30 bg-orange/10 text-orange hover:bg-orange/20'
              }`}
            >
              {condition.type === 'AND' ? 'ALL must be true (AND)' : 'ANY can be true (OR)'}
            </button>
          </div>
        )}

        {/* Children */}
        <div className="space-y-1.5">
          {condition.conditions.map((child, i) => {
            if (child.type === 'COMPARISON') {
              return (
                <ConditionRow
                  key={i}
                  cond={child}
                  path={`${path}.conditions.${i}`}
                  dispatch={dispatch}
                  onRemove={() => removeChild(i)}
                />
              );
            }

            // Nested sub-group — render as a distinct box
            if (child.type === 'AND' || child.type === 'OR') {
              return (
                <div key={i} className="flex items-start gap-1">
                  <div className={`flex-1 rounded-lg border border-glass-border bg-glass p-2.5`}>
                    <EnhancedConditionEditor
                      condition={child}
                      path={`${path}.conditions.${i}`}
                      dispatch={dispatch}
                      onOpenModal={onOpenModal}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => removeChild(i)}
                    className="w-[22px] h-[22px] rounded-md border border-glass-border text-ink-4 hover:border-red hover:text-red flex items-center justify-center cursor-pointer transition-colors flex-shrink-0 mt-2.5"
                    title="Remove group"
                  >
                    <X size={11} />
                  </button>
                </div>
              );
            }

            return null;
          })}
        </div>

        {/* Add buttons */}
        <div className="flex items-center gap-3 mt-2">
          <button
            type="button"
            onClick={() => onOpenModal({ type: 'addToGroup', groupPath: path })}
            className="flex items-center gap-1 px-2.5 py-1 rounded-md text-ink-4 hover:text-orange text-[9px] font-bold cursor-pointer transition-colors"
          >
            <Plus size={9} />
            Add Condition
          </button>
          <button
            type="button"
            onClick={() => onOpenModal({ type: 'addSubGroup', groupPath: path })}
            className="flex items-center gap-1 px-2.5 py-1 rounded-md text-ink-4 hover:text-orange text-[9px] font-bold cursor-pointer transition-colors"
          >
            <Plus size={9} />
            Add Group
          </button>
        </div>
      </div>
    );
  }

  return <em className="text-ink-4 text-[10px]">Unknown condition type</em>;
}

// ─── Live Rule Preview ───────────────────────────────────

function RulePreview({ condition }: { condition: Condition | undefined }) {
  if (!condition) return null;

  // Skip preview for trivial always-true
  if (condition.type === 'COMPARISON' && isAlwaysTrue(condition)) return null;

  return (
    <div className="rounded-lg bg-surface-2/50 border border-glass-border px-3 pt-2 pb-2.5 mt-2 space-y-1.5">
      <span className="text-[9px] font-bold text-ink-4 uppercase">Preview</span>
      <div className="text-[11px] text-ink-2 leading-relaxed">
        {condToSentence(condition)}
      </div>
    </div>
  );
}

// ─── Indicator Parameter Docs ────────────────────────────

interface ParamDoc { name: string; desc: string }

const INDICATOR_DOCS: Record<string, { about: string; params: ParamDoc[]; examples: string[] }> = {
  // Moving Averages
  RSI: { about: 'Relative Strength Index — momentum oscillator (0–100) measuring speed of price changes.', params: [{ name: 'Period', desc: 'Lookback candles. Standard 14. Lower = more reactive, higher = smoother.' }], examples: ['RSI(14) < 30 → classic oversold buy signal', 'RSI(7) for scalping on 5m charts (faster response)', 'RSI(21) for swing trading on 4h charts (smoother)'] },
  SMA: { about: 'Simple Moving Average — arithmetic mean of closing prices over the period.', params: [{ name: 'Period', desc: 'Number of candles to average. Common: 20 (short), 50 (medium), 200 (long).' }], examples: ['Price > SMA(200) → long-term uptrend filter', 'SMA(9) × above SMA(21) → golden cross entry', 'SMA(50) as dynamic support on 1h chart'] },
  EMA: { about: 'Exponential Moving Average — weighted towards recent prices for faster response.', params: [{ name: 'Period', desc: 'Smoothing lookback. Lower = more sensitive to recent price.' }], examples: ['EMA(9) × above EMA(21) → fast trend crossover', 'Price bouncing off EMA(50) → trend continuation entry', 'EMA(200) on daily chart → institutional trend filter'] },
  WMA: { about: 'Weighted Moving Average — linearly weights prices, newest gets highest weight.', params: [{ name: 'Period', desc: 'Lookback candles. Similar to EMA but with linear weighting.' }], examples: ['WMA(10) × above WMA(30) → weighted crossover', 'Price > WMA(20) → short-term trend confirmation'] },
  DEMA: { about: 'Double EMA — applies EMA twice to reduce lag while staying smooth.', params: [{ name: 'Period', desc: 'Base period for the double-smoothed calculation.' }], examples: ['DEMA(9) × above DEMA(21) → faster than EMA crossover', 'Use on 1m–5m charts where EMA lag costs pips'] },
  TEMA: { about: 'Triple EMA — triple-smoothed for near-zero lag in fast markets.', params: [{ name: 'Period', desc: 'Base period for the triple-smoothed calculation.' }], examples: ['TEMA(8) for scalping — almost zero lag', 'TEMA(21) × above SMA(50) → mixed-speed trend filter'] },
  HMA: { about: 'Hull Moving Average — ultra-smooth with minimal lag using weighted MA math.', params: [{ name: 'Period', desc: 'Base lookback. Internally uses period/2 and √period sub-calculations.' }], examples: ['HMA(9) for direction changes — turns before EMA', 'HMA slope change → early trend reversal detection'] },
  // Trend
  ADX: { about: 'Average Directional Index — measures trend strength (0–100), not direction.', params: [{ name: 'Period', desc: 'Smoothing period. Standard 14. Above 25 = trending, below 20 = ranging.' }], examples: ['ADX(14) > 25 → only trade when trending (avoid chop)', 'ADX(14) < 20 → market is ranging, use mean-reversion', 'ADX rising from 15 to 25 → new trend emerging'] },
  DMI_PLUS: { about: 'Positive Directional Indicator (+DI) — measures upward price movement strength.', params: [{ name: 'Period', desc: 'Smoothing period. Standard 14. Compare with DMI− for trend direction.' }], examples: ['+DI(14) > −DI(14) → buyers dominate, bullish bias', '+DI × above −DI with ADX > 25 → strong uptrend confirmed'] },
  DMI_MINUS: { about: 'Negative Directional Indicator (−DI) — measures downward price movement strength.', params: [{ name: 'Period', desc: 'Smoothing period. Standard 14. When above DMI+, sellers dominate.' }], examples: ['−DI(14) > +DI(14) → sellers dominate, bearish bias', '−DI × above +DI → trend reversal to the downside'] },
  AROON_UP: { about: 'Aroon Up — how recently price made a new high (0–100). 100 = just now.', params: [{ name: 'Period', desc: 'Lookback for the highest high. Standard 14–25.' }], examples: ['Aroon Up(25) > 70 → recent new highs, bullish', 'Aroon Up > Aroon Down → uptrend direction'] },
  AROON_DOWN: { about: 'Aroon Down — how recently price made a new low (0–100). 100 = just now.', params: [{ name: 'Period', desc: 'Lookback for the lowest low. Standard 14–25.' }], examples: ['Aroon Down(25) > 70 → recent new lows, bearish', 'Aroon Down < 30 → no new lows for a while, bullish'] },
  AROON_OSC: { about: 'Aroon Oscillator — difference between Aroon Up and Down (−100 to +100).', params: [{ name: 'Period', desc: 'Lookback period. Positive = bullish, negative = bearish.' }], examples: ['Aroon Osc(25) > 50 → strong bullish conviction', 'Aroon Osc × above 0 → trend turning bullish'] },
  PSAR: { about: 'Parabolic SAR — trailing stop-and-reverse dots that follow price.', params: [{ name: 'Period', desc: 'Not used directly — SAR uses acceleration factor.' }, { name: 'Step (accel)', desc: 'Starting acceleration. Standard 0.02. Higher = tighter trailing.' }, { name: 'Max accel', desc: 'Maximum acceleration cap. Standard 0.2.' }], examples: ['Price × above PSAR → SAR flips bullish, enter long', 'Step 0.01 for slower trailing (fewer whipsaws)', 'Step 0.03 for tighter trailing (quicker exits)'] },
  SUPERTREND: { about: 'Supertrend — ATR-based trend-following overlay. Above = bearish, below = bullish.', params: [{ name: 'Period', desc: 'ATR lookback. Standard 10.' }, { name: 'Multiplier', desc: 'ATR multiplier for band distance. Standard 3. Higher = wider bands, fewer signals.' }], examples: ['Price > Supertrend(10, 3) → bullish, ride the trend', 'Mult 2 for tighter entries on lower timeframes', 'Mult 4 for wider stops on daily charts'] },
  KST: { about: 'Know Sure Thing — weighted sum of 4 smoothed ROC periods. Confirms long-term trend.', params: [{ name: 'Period', desc: 'Base smoothing period for the combined ROC values.' }], examples: ['KST × above 0 → long-term momentum turning positive', 'Best on daily/weekly charts for swing trades'] },
  LINEAR_REG_SLOPE: { about: 'Linear Regression Slope — statistical trend direction. Positive = up, negative = down.', params: [{ name: 'Period', desc: 'Regression lookback. Longer = more stable but slower to react.' }], examples: ['Slope(14) > 0 → statistically confirmed uptrend', 'Slope(50) for filtering noise on lower timeframes'] },
  // Momentum
  MACD: { about: 'MACD Line — difference between fast and slow EMA. Crossing signal line = momentum shift.', params: [{ name: 'Period', desc: 'Slow EMA period (used for warmup).' }, { name: 'Fast', desc: 'Fast EMA period. Standard 12.' }, { name: 'Slow', desc: 'Slow EMA period. Standard 26.' }, { name: 'Signal', desc: 'Signal line EMA period. Standard 9.' }], examples: ['MACD × above Signal → bullish momentum shift', 'MACD(8,17,9) for faster signals on intraday', 'MACD > 0 → price above slow EMA, bullish bias'] },
  MACD_SIGNAL: { about: 'MACD Signal Line — smoothed EMA of the MACD line. Crossovers trigger entries.', params: [{ name: 'Period', desc: 'Slow EMA period.' }, { name: 'Fast', desc: 'Fast EMA. Standard 12.' }, { name: 'Slow', desc: 'Slow EMA. Standard 26.' }, { name: 'Signal', desc: 'Signal smoothing. Standard 9.' }], examples: ['Use with MACD line for crossover signals', 'Signal(5) for faster reactions on scalping'] },
  MACD_HISTOGRAM: { about: 'MACD Histogram — difference between MACD and Signal. Shows momentum acceleration.', params: [{ name: 'Period', desc: 'Slow EMA period.' }, { name: 'Fast', desc: 'Fast EMA. Standard 12.' }, { name: 'Slow', desc: 'Slow EMA. Standard 26.' }, { name: 'Signal', desc: 'Signal smoothing. Standard 9.' }], examples: ['Histogram > 0 → MACD above signal, momentum accelerating', 'Histogram shrinking → momentum fading, prepare for reversal'] },
  PPO: { about: 'Percentage Price Oscillator — MACD expressed as a percentage for cross-asset comparison.', params: [{ name: 'Period', desc: 'Slow EMA period.' }, { name: 'Fast', desc: 'Fast EMA. Standard 12.' }, { name: 'Slow', desc: 'Slow EMA. Standard 26.' }], examples: ['PPO × above 0 → normalized bullish momentum', 'Compare PPO across XAUUSD vs EURUSD (% makes it comparable)'] },
  STOCH_K: { about: 'Stochastic %K — shows where price closed relative to high-low range (0–100).', params: [{ name: 'Period', desc: '%K lookback. Standard 14.' }, { name: 'Signal', desc: '%D smoothing. Standard 3.' }], examples: ['%K(14) < 20 → oversold, look for bounce', '%K(5,3) for scalping — faster overbought/sold signals'] },
  STOCH_D: { about: 'Stochastic %D — smoothed %K signal line. Crossovers with %K confirm reversals.', params: [{ name: 'Period', desc: '%K lookback. Standard 14.' }, { name: 'Signal', desc: '%D smoothing. Standard 3.' }], examples: ['%K × above %D below 20 → bullish reversal signal', '%D(14,5) for smoother signal on noisy markets'] },
  STOCH_RSI: { about: 'Stochastic RSI — applies Stochastic formula to RSI values. Extra sensitive.', params: [{ name: 'Period', desc: 'RSI + Stochastic lookback. Standard 14.' }], examples: ['StochRSI(14) < 20 → deeply oversold, high-probability bounce', 'More sensitive than plain RSI — better for ranging markets'] },
  CCI: { about: 'Commodity Channel Index — measures price deviation from average. ±100 = overbought/sold.', params: [{ name: 'Period', desc: 'Lookback candles. Standard 20.' }], examples: ['CCI(20) < −100 → oversold, reversal setup', 'CCI(14) for faster signals on 15m charts', 'CCI > 200 → extremely overbought, high risk'] },
  WILLIAMS_R: { about: 'Williams %R — like Stochastic but inverted (−100 to 0). −80 = oversold, −20 = overbought.', params: [{ name: 'Period', desc: 'Lookback for high-low range. Standard 14.' }], examples: ['%R(14) < −80 → oversold buy zone', '%R(14) > −20 → overbought sell zone', '%R(10) for faster scalping signals'] },
  ROC: { about: 'Rate of Change — percentage price change over N periods. Above 0 = positive momentum.', params: [{ name: 'Period', desc: 'Lookback candles. Standard 12.' }], examples: ['ROC(12) > 0 → price higher than 12 candles ago', 'ROC(1) for tick-level momentum detection'] },
  MOMENTUM: { about: 'Raw Momentum — absolute price difference from N periods ago.', params: [{ name: 'Period', desc: 'Lookback candles. Standard 10.' }], examples: ['Momentum(10) > 0 → price trending upward', 'Momentum × above 0 → fresh upward impulse'] },
  AWESOME_OSC: { about: 'Awesome Oscillator — difference between 5 and 34 period midpoint SMAs.', params: [{ name: 'Period', desc: 'Base period for warmup.' }, { name: 'Fast', desc: 'Fast SMA. Standard 5.' }, { name: 'Slow', desc: 'Slow SMA. Standard 34.' }], examples: ['AO > 0 → bullish momentum zone', 'AO × above 0 → momentum just turned bullish', 'Twin peaks pattern: two dips above 0 → continuation'] },
  TRIX: { about: 'TRIX — triple-smoothed EMA rate of change. Filters noise for cleaner signals.', params: [{ name: 'Period', desc: 'Triple EMA smoothing period. Standard 15.' }], examples: ['TRIX(15) × above 0 → very clean trend change signal', 'Slow but reliable — best on 1h+ charts'] },
  DPO: { about: 'Detrended Price Oscillator — removes trend to show cycles. Not time-aligned.', params: [{ name: 'Period', desc: 'Lookback candles. Standard 20.' }], examples: ['DPO(20) > 0 → price above its detrended average', 'Useful for identifying cycle tops and bottoms'] },
  CMO: { about: 'Chande Momentum Oscillator — ratio of up vs down movement (−100 to +100).', params: [{ name: 'Period', desc: 'Lookback. Standard 14. ±50 = overbought/sold zones.' }], examples: ['CMO(14) < −50 → strong downward pressure, oversold', 'CMO(14) > 50 → strong upward pressure, overbought'] },
  ULTIMATE_OSC: { about: 'Ultimate Oscillator — combines 3 timeframes (fast/mid/slow) for balanced momentum.', params: [{ name: 'Period', desc: 'Mid period. Standard 14.' }, { name: 'Fast', desc: 'Short-term period. Standard 7.' }, { name: 'Slow', desc: 'Long-term period. Standard 28.' }], examples: ['UO(7,14,28) < 30 → oversold across all timeframes', 'UO divergence with price → strong reversal signal'] },
  COPPOCK: { about: 'Coppock Curve — long-term momentum indicator, originally for monthly charts.', params: [{ name: 'Period', desc: 'WMA smoothing. Standard 10.' }], examples: ['Coppock × above 0 → long-term buy signal', 'Originally monthly — use on daily for shorter timeframes'] },
  // Volatility
  ATR: { about: 'Average True Range — measures volatility in absolute price units.', params: [{ name: 'Period', desc: 'Smoothing period. Standard 14. Higher ATR = more volatile market.' }], examples: ['ATR(14) > 1.5 on EURUSD → high volatility, widen stops', 'Set SL at 2× ATR below entry for volatility-based stops', 'ATR(14) on XAUUSD ~ $20 means ±$20 typical range'] },
  BB_UPPER: { about: 'Bollinger Band Upper — SMA + N standard deviations. Price above = overbought/breakout.', params: [{ name: 'Period', desc: 'SMA lookback. Standard 20.' }, { name: 'StdDev (σ)', desc: 'Band width multiplier. Standard 2. Higher = wider bands.' }], examples: ['Price > BB Upper(20,2) → breakout or overbought', 'σ=1.5 for tighter bands (more signals)', 'σ=2.5 for wider bands (fewer but stronger signals)'] },
  BB_MIDDLE: { about: 'Bollinger Band Middle — the SMA centerline of the bands.', params: [{ name: 'Period', desc: 'SMA lookback. Standard 20.' }, { name: 'StdDev (σ)', desc: 'Band width. Standard 2 (not used for middle, but sets context).' }], examples: ['Price > BB Middle → above average, bullish bias', 'Mean-reversion: entry when price returns to middle'] },
  BB_LOWER: { about: 'Bollinger Band Lower — SMA − N standard deviations. Price below = oversold/breakdown.', params: [{ name: 'Period', desc: 'SMA lookback. Standard 20.' }, { name: 'StdDev (σ)', desc: 'Band width multiplier. Standard 2.' }], examples: ['Price < BB Lower(20,2) → oversold bounce opportunity', 'Bollinger squeeze (narrow bands) → breakout incoming'] },
  BB_STD: { about: 'Bollinger Band StdDev — the raw standard deviation value. Measures volatility width.', params: [{ name: 'Period', desc: 'Calculation lookback. Standard 20.' }], examples: ['Low BB StdDev → bands squeezing, breakout expected', 'High BB StdDev → volatile market, widen risk params'] },
  KELTNER_UPPER: { about: 'Keltner Channel Upper — EMA + ATR × multiplier. Smoother than Bollinger.', params: [{ name: 'Period', desc: 'EMA and ATR lookback. Standard 20.' }, { name: 'Multiplier', desc: 'ATR multiplier for band width. Standard 1.5.' }], examples: ['Price > Keltner Upper → strong momentum breakout', 'BB inside Keltner → squeeze setup (TTM Squeeze)'] },
  KELTNER_MID: { about: 'Keltner Channel Mid — the EMA centerline.', params: [{ name: 'Period', desc: 'EMA lookback. Standard 20.' }], examples: ['Price bouncing off Keltner Mid → trend continuation'] },
  KELTNER_LOWER: { about: 'Keltner Channel Lower — EMA − ATR × multiplier.', params: [{ name: 'Period', desc: 'EMA and ATR lookback. Standard 20.' }, { name: 'Multiplier', desc: 'ATR multiplier. Standard 1.5.' }], examples: ['Price < Keltner Lower → oversold or breakdown'] },
  DONCHIAN_UPPER: { about: 'Donchian Channel Upper — highest high over N periods. Turtle trading breakout level.', params: [{ name: 'Period', desc: 'Lookback for highest high. Standard 20.' }], examples: ['Price > Donchian Upper(20) → 20-day breakout (turtle entry)', 'Donchian(55) for system 2 turtle trading rules'] },
  DONCHIAN_LOWER: { about: 'Donchian Channel Lower — lowest low over N periods.', params: [{ name: 'Period', desc: 'Lookback for lowest low. Standard 20.' }], examples: ['Price < Donchian Lower(20) → downside breakout', 'Use as trailing stop for long positions'] },
  DONCHIAN_MID: { about: 'Donchian Channel Mid — average of upper and lower channel.', params: [{ name: 'Period', desc: 'Lookback. Standard 20.' }], examples: ['Price > Donchian Mid → above the range midpoint'] },
  CHANDELIER_EXIT_LONG: { about: 'Chandelier Exit Long — highest high minus ATR × multiplier. Trailing stop for longs.', params: [{ name: 'Period', desc: 'ATR and high lookback. Standard 22.' }, { name: 'Multiplier', desc: 'ATR multiplier. Standard 3. Higher = wider stop.' }], examples: ['Price < Chandelier Exit → exit long positions', 'Mult 2 for tight trailing, mult 4 for loose trailing'] },
  CHANDELIER_EXIT_SHORT: { about: 'Chandelier Exit Short — lowest low plus ATR × multiplier. Trailing stop for shorts.', params: [{ name: 'Period', desc: 'ATR and low lookback. Standard 22.' }, { name: 'Multiplier', desc: 'ATR multiplier. Standard 3.' }], examples: ['Price > Chandelier Exit Short → exit short positions'] },
  // Range & Volume
  RANGE_HIGH: { about: 'Range High — highest price over the lookback period.', params: [{ name: 'Period', desc: 'Number of candles. Standard 20.' }], examples: ['Price > Range High(20) → breakout above recent range'] },
  RANGE_LOW: { about: 'Range Low — lowest price over the lookback period.', params: [{ name: 'Period', desc: 'Number of candles. Standard 20.' }], examples: ['Price < Range Low(20) → breakdown below recent range'] },
  RANGE_WIDTH: { about: 'Range Width — difference between range high and low. Measures consolidation.', params: [{ name: 'Period', desc: 'Lookback candles. Standard 20.' }], examples: ['Small Range Width → market consolidating, breakout soon'] },
  HIGHEST_HIGH: { about: 'Highest High — absolute highest high over N candles. Breakout reference.', params: [{ name: 'Period', desc: 'Lookback candles. Standard 20.' }], examples: ['Price > Highest High(50) → new 50-bar high, strong breakout'] },
  LOWEST_LOW: { about: 'Lowest Low — absolute lowest low over N candles. Breakdown reference.', params: [{ name: 'Period', desc: 'Lookback candles. Standard 20.' }], examples: ['Price < Lowest Low(50) → new 50-bar low, breakdown'] },
  VOLUME_SMA: { about: 'Volume SMA — average volume over N periods. Compare with current for spikes.', params: [{ name: 'Period', desc: 'Average lookback. Standard 20.' }], examples: ['Volume > 2× Volume SMA(20) → unusual volume spike', 'Low volume below SMA → thin market, avoid entries'] },
  OBV: { about: 'On Balance Volume — cumulative volume flow. Rising OBV = accumulation.', params: [{ name: 'Period', desc: 'Smoothing period for OBV trend.' }], examples: ['OBV rising while price flat → accumulation (bullish divergence)', 'OBV falling while price rises → distribution warning'] },
  VWAP: { about: 'Volume-Weighted Average Price — fair value based on volume. Institutional benchmark.', params: [{ name: 'Period', desc: 'Reset/lookback period. Standard 20.' }], examples: ['Price > VWAP → above fair value, institutions buying', 'Intraday: buy dips to VWAP in uptrend'] },
  MFI: { about: 'Money Flow Index — RSI-like but volume-weighted (0–100). 20/80 = oversold/overbought.', params: [{ name: 'Period', desc: 'Lookback candles. Standard 14.' }], examples: ['MFI(14) < 20 → selling exhausted with volume confirmation', 'MFI divergence from price → strong reversal signal'] },
  CMF: { about: 'Chaikin Money Flow — volume-weighted close position (−1 to +1). Above 0 = buying.', params: [{ name: 'Period', desc: 'Lookback candles. Standard 20.' }], examples: ['CMF(20) > 0.1 → sustained buying pressure', 'CMF(20) < −0.1 → sustained selling pressure'] },
  ADL: { about: 'Accumulation/Distribution Line — cumulative volume flow based on close location.', params: [{ name: 'Period', desc: 'Smoothing period.' }], examples: ['ADL rising → money flowing into the asset', 'ADL divergence from price → early reversal warning'] },
  FORCE_INDEX: { about: 'Force Index — price change × volume. Positive = bull pressure, negative = bear.', params: [{ name: 'Period', desc: 'EMA smoothing. Standard 13.' }], examples: ['Force(13) > 0 → bulls have the edge', 'Force × above 0 → momentum just turned bullish'] },
  // Ichimoku
  ICHIMOKU_TENKAN: { about: 'Tenkan-sen (Conversion) — midpoint of 9-period high-low. Short-term signal.', params: [{ name: 'Conversion', desc: 'Short-term lookback. Standard 9.' }, { name: 'Base', desc: 'Medium-term lookback. Standard 26.' }, { name: 'Span', desc: 'Long-term lookback. Standard 52.' }], examples: ['Tenkan × above Kijun → bullish TK cross', 'Price bouncing off Tenkan → short-term trend continuation'] },
  ICHIMOKU_KIJUN: { about: 'Kijun-sen (Base) — midpoint of 26-period high-low. Medium-term trend.', params: [{ name: 'Conversion', desc: 'Standard 9.' }, { name: 'Base', desc: 'Standard 26.' }, { name: 'Span', desc: 'Standard 52.' }], examples: ['Price > Kijun → medium-term bullish bias', 'Flat Kijun → market is ranging, expect breakout'] },
  ICHIMOKU_SENKOU_A: { about: 'Senkou Span A — average of Tenkan and Kijun, plotted 26 periods ahead. Cloud boundary.', params: [{ name: 'Conversion', desc: 'Standard 9.' }, { name: 'Base', desc: 'Standard 26.' }, { name: 'Span', desc: 'Standard 52.' }], examples: ['Price > Senkou A AND Senkou B → above the cloud (bullish)', 'Senkou A > Senkou B → green cloud, bullish bias'] },
  ICHIMOKU_SENKOU_B: { about: 'Senkou Span B — midpoint of 52-period high-low, plotted 26 ahead. Cloud boundary.', params: [{ name: 'Conversion', desc: 'Standard 9.' }, { name: 'Base', desc: 'Standard 26.' }, { name: 'Span', desc: 'Standard 52.' }], examples: ['Thick cloud (A far from B) → strong support/resistance', 'Thin cloud → weak support, easier breakthrough'] },
  ICHIMOKU_CHIKOU: { about: 'Chikou Span (Lagging) — current close plotted 26 periods back. Confirms trend.', params: [{ name: 'Conversion', desc: 'Standard 9.' }, { name: 'Base', desc: 'Standard 26.' }, { name: 'Span', desc: 'Standard 52.' }], examples: ['Chikou above price from 26 bars ago → confirms uptrend'] },
  // Elder Ray & Pivots
  ELDER_RAY_BULL: { about: 'Elder Ray Bull Power — high minus EMA. Positive = buyers pushing above average.', params: [{ name: 'Period', desc: 'EMA lookback. Standard 13.' }], examples: ['Bull Power > 0 → buyers pushing highs above EMA', 'Rising Bull Power → increasing buyer strength'] },
  ELDER_RAY_BEAR: { about: 'Elder Ray Bear Power — low minus EMA. Negative = sellers pushing below average.', params: [{ name: 'Period', desc: 'EMA lookback. Standard 13.' }], examples: ['Bear Power < 0 (normal) — rising toward 0 → sellers weakening', 'Bear Power > 0 → rare, extreme bullish'] },
  PIVOT_PP: { about: 'Pivot Point — (High + Low + Close) / 3 from previous period. Key support/resistance.', params: [{ name: 'Period', desc: 'Lookback for H/L/C calculation. Standard 1 (daily).' }], examples: ['Price > PP → bullish intraday bias', 'PP as intraday entry target or stop level'] },
  PIVOT_R1: { about: 'Resistance 1 — first resistance level above pivot. Common reversal zone.', params: [{ name: 'Period', desc: 'Standard 1 (daily).' }], examples: ['Price approaching R1 → take profit zone', 'Price breaks R1 → target R2 next'] },
  PIVOT_S1: { about: 'Support 1 — first support level below pivot. Common bounce zone.', params: [{ name: 'Period', desc: 'Standard 1 (daily).' }], examples: ['Price at S1 → look for bounce entries', 'Price breaks S1 → target S2, bearish'] },
  PIVOT_R2: { about: 'Resistance 2 — second resistance. Stronger barrier than R1.', params: [{ name: 'Period', desc: 'Standard 1 (daily).' }], examples: ['Price at R2 → strong resistance, high-probability reversal'] },
  PIVOT_S2: { about: 'Support 2 — second support. Stronger floor than S1.', params: [{ name: 'Period', desc: 'Standard 1 (daily).' }], examples: ['Price at S2 → strong support, high-probability bounce'] },
  // Price Derivatives
  MEDIAN_PRICE: { about: 'Median Price — (High + Low) / 2. Simple measure of the midpoint.', params: [{ name: 'Period', desc: 'Lookback. Typically 1 for current candle.' }], examples: ['Use as input for other indicators instead of close price'] },
  TYPICAL_PRICE: { about: 'Typical Price — (High + Low + Close) / 3. Commonly used as a proxy for fair value.', params: [{ name: 'Period', desc: 'Lookback. Typically 1.' }], examples: ['Typical Price > SMA → price action above average'] },
  WEIGHTED_CLOSE: { about: 'Weighted Close — (High + Low + 2×Close) / 4. Emphasizes close price.', params: [{ name: 'Period', desc: 'Lookback. Typically 1.' }], examples: ['Puts more weight on the close — where the real consensus is'] },
  // Candle Analysis
  CANDLE_RANGE: { about: 'Candle Range — the total price movement within the current candle (high minus low). Includes both the body and the wicks. Measures how active/volatile the current candle is.', params: [{ name: 'Period', desc: 'Not used. Offset determines which candle (0 = current, 1 = previous).' }], examples: ['CANDLE_RANGE > 10 → only trade when candle is active enough', 'CANDLE_RANGE < 50 → skip extremely volatile candles', 'CANDLE_RANGE offset=0 > CANDLE_RANGE offset=1 → current candle is bigger than previous'] },
  CANDLE_BODY: { about: 'Candle Body — the directional price movement |close - open|, ignoring wicks. A large body means strong momentum in one direction. A small body (doji) means indecision.', params: [{ name: 'Period', desc: 'Not used. Offset determines which candle.' }], examples: ['CANDLE_BODY > 8 → strong directional move, good for trend entries', 'CANDLE_BODY < 2 → doji/indecision candle, avoid entries', 'Large range + small body → spike/rejection (fake move)'] },
  SPIKE_RATIO: { about: 'Spike Ratio — current candle range divided by the average range of the last N candles. Values above 2.0 indicate an abnormally large candle (spike). Use this to filter out entries during volatile spikes on Jump indices and other synthetic instruments.', params: [{ name: 'Period', desc: 'Number of previous candles to average. Standard 20.' }], examples: ['SPIKE_RATIO(20) < 2.5 → skip entries during spikes (entry gate)', 'SPIKE_RATIO(20) > 3.0 → detect extreme spikes for breakout entries', 'On Jump 50: a "jump" creates SPIKE_RATIO > 3 — filter it to avoid bad entries'] },
};

/** Extract indicator names from a condition tree */
function extractIndicators(condition: Condition): string[] {
  const indicators: string[] = [];
  function walk(c: Condition) {
    if (c.type === 'COMPARISON') {
      if (c.left.type === 'INDICATOR' && c.left.indicator) indicators.push(c.left.indicator);
      if (c.right.type === 'INDICATOR' && c.right.indicator) indicators.push(c.right.indicator);
    } else if (c.type === 'AND' || c.type === 'OR') {
      c.conditions.forEach(walk);
    }
  }
  walk(condition);
  return [...new Set(indicators)];
}

// ─── Modal Types ────────────────────────────────────────

type ModalMode =
  | { type: 'replace'; path: string }
  | { type: 'addToGroup'; groupPath: string }
  | { type: 'wrapAndAdd'; path: string }
  | { type: 'addSubGroup'; groupPath: string };

// ─── Add Condition Modal ────────────────────────────────

const INDICATOR_GROUPS = ['Momentum', 'Trend', 'Ichimoku', 'Pivot Points', 'Volatility', 'Volume'];

function ConditionButton({
  preset,
  onSelect,
  onClose,
  onHover,
  isHovered,
}: {
  preset: ConditionPreset;
  onSelect: (c: Condition) => void;
  onClose: () => void;
  onHover: (id: string | null) => void;
  isHovered: boolean;
}) {
  return (
    <button
      onClick={() => { onSelect(preset.condition); onClose(); }}
      onMouseEnter={() => onHover(preset.id)}
      className={`w-full flex items-center justify-between rounded-lg border px-3 py-2 text-left cursor-pointer transition-all group ${
        isHovered
          ? 'border-orange/40 bg-orange/5'
          : 'border-glass-border bg-surface hover:border-orange/40 hover:bg-orange/5'
      }`}
    >
      <span className={`text-[11px] font-bold transition-colors ${isHovered ? 'text-orange' : 'text-ink group-hover:text-orange'}`}>
        {preset.name}
      </span>
      <span className="text-[9px] text-ink-4 font-mono">{preset.desc}</span>
    </button>
  );
}

function AddConditionModal({
  mode: _mode,
  onSelect,
  onClose,
}: {
  mode: ModalMode;
  onSelect: (condition: Condition) => void;
  onClose: () => void;
}) {
  const [activeCategory, setActiveCategory] = useState<PresetCategory>('indicators');
  const [searchQuery, setSearchQuery] = useState('');
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const filtered = searchQuery
    ? PRESETS.filter((p) => p.name.toLowerCase().includes(searchQuery.toLowerCase()) || p.desc.toLowerCase().includes(searchQuery.toLowerCase()) || p.tip.toLowerCase().includes(searchQuery.toLowerCase()))
    : PRESETS.filter((p) => p.category === activeCategory);

  const hoveredPreset = hoveredId ? PRESETS.find((p) => p.id === hoveredId) : null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center" onClick={onClose}>
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />

      {/* Modal — wider on desktop for two-panel layout */}
      <div
        className="relative z-10 w-[90vw] max-w-lg lg:max-w-3xl mx-4 h-[560px] flex flex-col rounded-xl border border-glass-border shadow-modal overflow-hidden backdrop-blur-xl bg-bg-2"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-border">
          <div className="flex items-center gap-2">
            <Plus size={14} className="text-orange" />
            <span className="text-sm font-extrabold">Add Condition</span>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-7 h-7 rounded-lg flex items-center justify-center text-ink-4 hover:text-ink hover:bg-glass-2 cursor-pointer transition-colors"
          >
            <X size={14} />
          </button>
        </div>

        {/* Two-panel body */}
        <div className="flex flex-1 min-h-0">
          {/* Left panel — condition list */}
          <div className="flex-1 flex flex-col min-w-0">
            {/* Search */}
            <div className="px-5 pt-3">
              <div className="flex items-center gap-2 bg-surface border border-glass-border rounded-lg px-3 py-2">
                <Search size={12} className="text-ink-4" />
                <input
                  type="text"
                  placeholder="Search conditions..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="flex-1 bg-transparent text-[11px] text-ink outline-none placeholder:text-ink-4"
                  autoFocus
                />
              </div>
            </div>

            {/* Category tabs */}
            {!searchQuery && (
              <div className="flex items-center gap-1 px-5 py-2.5 overflow-x-auto">
                {PRESET_CATEGORIES.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => setActiveCategory(cat.id)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-bold whitespace-nowrap cursor-pointer transition-colors border-none ${
                      activeCategory === cat.id
                        ? 'bg-orange/15 text-orange'
                        : 'text-ink-3 hover:bg-surface hover:text-ink'
                    }`}
                  >
                    {cat.icon}
                    {cat.label}
                  </button>
                ))}
              </div>
            )}

            {/* Preset list */}
            <div className="px-5 py-3 flex-1 min-h-0 overflow-y-auto">
              {activeCategory === 'indicators' && !searchQuery ? (
                <div className="space-y-3">
                  {INDICATOR_GROUPS.map((group) => {
                    const items = filtered.filter((p) => p.subCategory === group);
                    if (items.length === 0) return null;
                    return (
                      <div key={group}>
                        <p className="text-[9px] font-bold text-ink-4 uppercase tracking-wider mb-1.5">{group}</p>
                        <div className="space-y-1">
                          {items.map((preset) => (
                            <ConditionButton
                              key={preset.id}
                              preset={preset}
                              onSelect={onSelect}
                              onClose={onClose}
                              onHover={setHoveredId}
                              isHovered={hoveredId === preset.id}
                            />
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="space-y-1">
                  {filtered.map((preset) => (
                    <ConditionButton
                      key={preset.id}
                      preset={preset}
                      onSelect={onSelect}
                      onClose={onClose}
                      onHover={setHoveredId}
                      isHovered={hoveredId === preset.id}
                    />
                  ))}
                  {filtered.length === 0 && (
                    <p className="text-center text-[10px] text-ink-4 py-4">No matching conditions</p>
                  )}
                </div>
              )}
            </div>

            {/* Custom starters */}
            <div className="border-t border-border px-5 py-3 space-y-1.5">
              <p className="text-[9px] font-bold text-ink-4 uppercase tracking-wider mb-2">Start from scratch</p>
              <div className="grid grid-cols-3 gap-1.5">
                {([
                  { label: 'Price', left: { type: 'PRICE' as const, field: 'CURRENT' }, right: { type: 'CONSTANT' as const, value: 0 } },
                  { label: 'Indicator', left: { type: 'INDICATOR' as const, indicator: 'RSI', params: { period: 14 } }, right: { type: 'CONSTANT' as const, value: 50 } },
                  { label: 'Position', left: { type: 'POSITION' as const, field: 'COUNT' }, right: { type: 'CONSTANT' as const, value: 0 } },
                  { label: 'Account', left: { type: 'ACCOUNT' as const, field: 'EQUITY' }, right: { type: 'CONSTANT' as const, value: 500 } },
                  { label: 'Time', left: { type: 'TIME' as const, field: 'HOUR' }, right: { type: 'CONSTANT' as const, value: 8 } },
                  { label: 'Number', left: { type: 'CONSTANT' as const, value: 0 }, right: { type: 'CONSTANT' as const, value: 0 } },
                ] as { label: string; left: ValueRef; right: ValueRef }[]).map((s) => (
                  <button
                    key={s.label}
                    type="button"
                    onClick={() => {
                      onSelect({ type: 'COMPARISON', left: s.left, op: '>', right: s.right });
                      onClose();
                    }}
                    className="px-2.5 py-1.5 rounded-lg border border-glass-border text-ink-3 hover:border-orange hover:text-orange text-[10px] font-bold cursor-pointer transition-colors text-center"
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Right panel — description (desktop only) */}
          <div className="hidden lg:flex w-[280px] flex-shrink-0 border-l border-border flex-col overflow-y-auto">
            {hoveredPreset ? (() => {
              const indicators = extractIndicators(hoveredPreset.condition);
              return (
                <div className="p-5 animate-fade-in">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-7 h-7 rounded-lg bg-orange/10 flex items-center justify-center flex-shrink-0">
                      <Activity size={14} className="text-orange" />
                    </div>
                    <div>
                      <p className="text-[12px] font-extrabold text-ink">{hoveredPreset.name}</p>
                      <p className="text-[9px] font-mono text-ink-4">{hoveredPreset.desc}</p>
                    </div>
                  </div>
                  <div className="rounded-lg bg-surface border border-glass-border p-3 mb-3">
                    <p className="text-[11px] text-ink-2 leading-relaxed">{hoveredPreset.tip}</p>
                  </div>

                  {/* Indicator parameter docs */}
                  {indicators.map((ind) => {
                    const doc = INDICATOR_DOCS[ind];
                    if (!doc) return null;
                    return (
                      <div key={ind} className="mb-3">
                        <p className="text-[10px] font-extrabold text-orange mb-1">{ind}</p>
                        <p className="text-[10px] text-ink-3 leading-relaxed mb-2">{doc.about}</p>
                        <div className="space-y-1.5 mb-2">
                          {doc.params.map((p) => (
                            <div key={p.name} className="flex gap-2">
                              <span className="text-[9px] font-bold text-ink-2 min-w-[60px] flex-shrink-0">{p.name}</span>
                              <span className="text-[9px] text-ink-4 leading-relaxed">{p.desc}</span>
                            </div>
                          ))}
                        </div>
                        {doc.examples.length > 0 && (
                          <div className="rounded-md bg-orange/5 border border-orange/10 px-2.5 py-2">
                            <p className="text-[8px] font-bold text-orange/60 uppercase tracking-wider mb-1">Examples</p>
                            <ul className="space-y-1">
                              {doc.examples.map((ex, i) => (
                                <li key={i} className="text-[9px] text-ink-3 leading-relaxed pl-2 border-l border-orange/20">{ex}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {/* Offset — universal param, pinned at bottom */}
                  {indicators.length > 0 && (
                    <div className="mt-1 rounded-lg border border-dashed border-ink-4/20 px-3 py-2.5">
                      <p className="text-[9px] font-extrabold text-ink-3 mb-1">Offset <span className="font-normal text-ink-4">· all indicators</span></p>
                      <p className="text-[9px] text-ink-4 leading-relaxed">
                        Looks back N candles. <span className="text-ink-3 font-semibold">0</span> = now, <span className="text-ink-3 font-semibold">1</span> = prev candle, <span className="text-ink-3 font-semibold">5</span> = five ago. Max <span className="text-ink-3 font-semibold">200</span>.
                      </p>
                      <p className="text-[9px] text-ink-4 leading-relaxed mt-1 pl-2 border-l-2 border-orange/20">
                        RSI(14) offset 5 &gt; RSI(14) offset 0 → RSI was higher 5 bars ago (momentum fading)
                      </p>
                    </div>
                  )}
                </div>
              );
            })() : (
              <div className="flex-1 flex items-center justify-center p-5">
                <div className="text-center">
                  <div className="w-10 h-10 rounded-xl bg-surface-2 flex items-center justify-center mx-auto mb-3">
                    <Search size={16} className="text-ink-4" />
                  </div>
                  <p className="text-[11px] text-ink-4 leading-relaxed">
                    Hover over a condition<br />to see its description
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

// ─── Inner Strategy Form ─────────────────────────────────

function StrategyForm({
  initialRules,
  initialMeta,
  isEdit,
  strategyId,
  onBackToTemplates,
  initialGridOpen,
  initialChatOpen,
  templateId,
}: {
  initialRules: ConditionEngineRules;
  initialMeta: { name: string; symbol: string; description: string };
  isEdit: boolean;
  strategyId?: string;
  onBackToTemplates?: () => void;
  initialGridOpen?: boolean;
  initialChatOpen?: boolean;
  templateId?: string;
}) {
  const navigate = useNavigate();
  const [draft, dispatch] = useReducer(formReducer, initialRules, (init) => structuredClone(init));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Validate offsets across all rules
  const MAX_OFFSET = 200;
  const hasOffsetError = useMemo(() => {
    const checkRef = (ref: any): boolean =>
      ref && (ref.offset ?? 0) > MAX_OFFSET;
    const walkCond = (c: any): boolean => {
      if (!c || typeof c !== 'object') return false;
      if (c.type === 'AND' || c.type === 'OR')
        return (c.conditions || []).some(walkCond);
      if (c.type === 'COMPARISON')
        return checkRef(c.left) || checkRef(c.right);
      return false;
    };
    const entry = (draft as any).entry;
    const exit = (draft as any).exit;
    for (const r of entry?.rules || []) {
      if (walkCond(r.conditions) || walkCond(r.buyCondition) || walkCond(r.sellCondition)) return true;
    }
    for (const r of exit?.rules || []) {
      if (walkCond(r.conditions)) return true;
    }
    return false;
  }, [draft]);

  // Walkthrough state — auto-start on first visit in create mode
  const [showTour, setShowTour] = useState(() => {
    if (isEdit) return false;
    return localStorage.getItem('strategy-editor-tour-seen') !== 'true';
  });
  const completeTour = useCallback(() => {
    setShowTour(false);
    localStorage.setItem('strategy-editor-tour-seen', 'true');
  }, []);

  // Metadata state
  const [name, setName] = useState(initialMeta.name);
  const [symbol, setSymbol] = useState(initialMeta.symbol);
  const [description, setDescription] = useState(initialMeta.description);
  const [nameError, setNameError] = useState('');
  const [symbolError, setSymbolError] = useState('');

  // Mutations
  const createStrategy = useCreateStrategy();
  const updateConfig = useUpdateConfig();
  const updateStrategy = useUpdateStrategy();

  // Modal state
  const [modalMode, setModalMode] = useState<ModalMode | null>(null);

  const openModal = useCallback((mode: ModalMode) => setModalMode(mode), []);
  const closeModal = useCallback(() => setModalMode(null), []);

  const handleModalSelect = useCallback((condition: Condition) => {
    if (!modalMode) return;
    if (modalMode.type === 'replace') {
      dispatch({ type: 'REPLACE_CONDITION', path: modalMode.path, condition });
    } else if (modalMode.type === 'wrapAndAdd') {
      dispatch({ type: 'WRAP_AND_ADD', path: modalMode.path, condition });
    } else if (modalMode.type === 'addSubGroup') {
      dispatch({ type: 'ADD_SUB_GROUP', groupPath: modalMode.groupPath, condition });
    } else {
      dispatch({ type: 'ADD_TO_GROUP', groupPath: modalMode.groupPath, condition });
    }
  }, [modalMode]);

  // Chat panel state
  const [chatOpen, setChatOpen] = useState(initialChatOpen ?? false);

  const handleApplyRulesFromChat = useCallback((rules: ConditionEngineRules) => {
    // Preserve user-configured fields that the AI doesn't manage.
    // The AI replaces the full rule set, but these fields are set via the
    // form UI and would be lost if we did a blind INIT.
    if (draft?.entry?.rules) {
      for (let i = 0; i < rules.entry.rules.length; i++) {
        const prev = draft.entry.rules[i];
        if (!prev) continue;
        const next = rules.entry.rules[i];
        if (!next) continue;
        // Preserve orderType/limitOffsetPips if AI didn't set them
        if (!next.orderType && prev.orderType) {
          next.orderType = prev.orderType;
          next.limitOffsetPips = prev.limitOffsetPips;
        }
        // Preserve cooldownCandles if AI didn't set it
        if (next.cooldownCandles == null && prev.cooldownCandles != null) {
          next.cooldownCandles = prev.cooldownCandles;
        }
      }
    }
    // Preserve grid config if AI didn't include it but user had it configured
    if (!rules.entry.grid && draft?.entry?.grid?.enabled) {
      rules.entry.grid = draft.entry.grid;
    }
    // Preserve trailing stop if AI didn't include it
    if (!rules.exit.trailingStop && draft?.exit?.trailingStop?.enabled) {
      rules.exit.trailingStop = draft.exit.trailingStop;
    }
    dispatch({ type: 'INIT', payload: rules });
  }, [draft]);

  // Accordion state
  const [sections, setSections] = useState({
    market: true,
    entry: true,
    grid: initialGridOpen ?? false,
    exit: false,
    trailing: false,
    risk: false,
  });

  const toggle = (key: keyof typeof sections) =>
    setSections((s) => ({ ...s, [key]: !s[key] }));

  const expandSection = useCallback((key: string) => {
    setSections((s) => ({ ...s, [key]: true }));
  }, []);

  const handleSave = async () => {
    setError(null);
    setNameError('');
    setSymbolError('');

    const trimmedName = name.trim();
    const trimmedSymbol = symbol.trim();

    if (!trimmedName) {
      setNameError('Name is required');
      return;
    }
    if (!isEdit && !trimmedSymbol) {
      setSymbolError('Symbol is required');
      return;
    }

    setSaving(true);
    try {
      if (isEdit && strategyId) {
        // Update rules + metadata in parallel
        await Promise.all([
          updateConfig.mutateAsync({ id: strategyId, rules: draft }),
          updateStrategy.mutateAsync({ id: strategyId, name: trimmedName, description: description.trim() || undefined, symbol: symbol || undefined }),
        ]);
        navigate(`/strategies/${strategyId}`);
      } else {
        const result = await createStrategy.mutateAsync({
          name: trimmedName,
          symbol: trimmedSymbol,
          description: description.trim() || undefined,
          strategy_type: 'CONDITION_ENGINE',
          rules: draft,
        });
        navigate(`/strategies/${result.id}`);
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to save strategy';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  if (!draft) return null;

  const backLink = onBackToTemplates ? (
    <button
      type="button"
      onClick={onBackToTemplates}
      className="inline-flex items-center gap-1.5 text-[10px] font-semibold text-ink-3 hover:text-orange transition-colors cursor-pointer"
    >
      <ArrowLeft size={12} />
      Back to Templates
    </button>
  ) : (
    <Link
      to={isEdit && strategyId ? `/strategies/${strategyId}` : '/strategies'}
      className="inline-flex items-center gap-1.5 text-[10px] font-semibold text-ink-3 hover:text-orange transition-colors"
    >
      <ArrowLeft size={12} />
      {isEdit ? 'Back to Strategy' : 'All Strategies'}
    </Link>
  );

  const headerBar = (
    <div className="flex items-center gap-3">
      <div className="w-10 h-10 rounded-md bg-surface-2 flex items-center justify-center">
        <Sparkles size={18} className="text-orange" />
      </div>
      <div className="flex-1">
        <h1 className="text-lg font-extrabold tracking-tight">
          {isEdit ? 'Edit Strategy' : 'New Strategy'}
        </h1>
        <p className="text-xs text-ink-3">
          {isEdit ? 'Modify rules and save changes' : 'Build a strategy from scratch'}
        </p>
      </div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => setChatOpen(!chatOpen)}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-[10px] font-bold cursor-pointer transition-all ${
            chatOpen
              ? 'border-orange/40 bg-orange/10 text-orange shadow-glow'
              : 'border-orange/20 text-orange hover:border-orange/40 hover:bg-orange/10'
          }`}
        >
          <Bot size={12} />
          AI Chat
        </button>
        <button
          type="button"
          onClick={() => setShowTour(true)}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-glass-border text-ink-4 hover:text-orange hover:border-orange/40 text-[10px] font-bold cursor-pointer transition-colors"
        >
          <HelpCircle size={12} />
          Tour
        </button>
      </div>
    </div>
  );

  // ─── Layout ───
  return (
    <div className={`${chatOpen ? '' : 'max-w-2xl'} mx-auto transition-all`}>
    {/* Back + Header — always full width */}
    <div className="space-y-4 mb-5">
      {backLink}
      {headerBar}
    </div>

    <div className={`flex items-start gap-6`}>
    <div className={`space-y-5 ${chatOpen ? 'flex-1 min-w-0 max-w-2xl' : 'w-full'}`}>
      {/* Add Condition Modal */}
      {modalMode && (
        <AddConditionModal
          mode={modalMode}
          onSelect={handleModalSelect}
          onClose={closeModal}
        />
      )}

      {/* Walkthrough */}
      <EditorWalkthrough active={showTour} onComplete={completeTour} onExpandSection={expandSection} />

      {/* Metadata Section */}
      <div className="glass-sm rounded-lg border border-glass-border p-4 space-y-3" data-tour="details">
        <div>
          <label className="block text-[10px] font-bold text-ink-4 uppercase tracking-wider mb-1">Name *</label>
          <input
            type="text"
            value={name}
            onChange={(e) => { setName(e.target.value); setNameError(''); }}
            placeholder="e.g., RSI Mean Reversion"
            className={`w-full bg-surface border rounded-lg px-3 py-2 text-sm font-mono text-ink outline-none transition-all duration-200 focus:border-orange focus:shadow-[0_0_8px_rgba(249,115,22,0.15)] placeholder:text-ink-4 ${nameError ? 'border-red' : 'border-glass-border'}`}
          />
          {nameError && <p className="text-[10px] text-red mt-1">{nameError}</p>}
        </div>
        <div>
          <label className="block text-[10px] font-bold text-ink-4 uppercase tracking-wider mb-1">Symbol *</label>
          <SymbolPicker
            value={symbol}
            onChange={(s) => { setSymbol(s); setSymbolError(''); }}
            error={symbolError}
          />
        </div>
        <div>
          <label className="block text-[10px] font-bold text-ink-4 uppercase tracking-wider mb-1">Description</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Optional description of the strategy"
            rows={2}
            className="w-full bg-surface border border-glass-border rounded-lg px-3 py-2 text-sm font-mono text-ink outline-none transition-all duration-200 focus:border-orange focus:shadow-[0_0_8px_rgba(249,115,22,0.15)] resize-none placeholder:text-ink-4"
          />
        </div>
      </div>

      {/* Rule Editor */}
      <div className="space-y-0">
        {/* Market Data */}
        <AccordionSection
          title="Market Data"
          icon={<Activity size={14} />}
          iconColor="text-blue"
          isOpen={sections.market}
          onToggle={() => toggle('market')}
        >
          <div data-tour="timeframe">
            <EditorField label={
              <span className="flex items-center gap-1">
                Timeframe
                <TimeframeInfo />
              </span>
            }>
              <StyledSelect
                value={draft.market_data.timeframe}
                onChange={(v) => dispatch({ type: 'SET_PATH', path: 'market_data.timeframe', value: v })}
                options={TIMEFRAMES.map((t) => ({ value: t, label: t }))}
                className="!w-[120px]"
              />
            </EditorField>
          </div>
        </AccordionSection>

        {/* Entry Rules */}
        <AccordionSection
          title="Entry Rules"
          icon={<TrendingUp size={14} />}
          iconColor="text-green"
          isOpen={sections.entry}
          onToggle={() => toggle('entry')}
          dataTour="entry-rules"
        >
          {draft.entry.rules.map((rule, ri) => {
            return (
              <div
                key={ri}
                className="rounded-[12px] p-3 border mb-2.5 glass-sm border-glass-border"
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[11px] font-extrabold text-ink-2">
                    {rule.id || `Rule ${ri + 1}`}
                  </span>
                  {draft.entry.rules.length > 1 && (
                    <button
                      type="button"
                      onClick={() => dispatch({ type: 'REMOVE_ENTRY_RULE', index: ri })}
                      className="text-[10px] font-bold text-red/70 hover:text-red cursor-pointer transition-colors"
                    >
                      Remove
                    </button>
                  )}
                </div>

                <EditorField label={
                  <span className="flex items-center gap-1">
                    Name
                    <EntryNameInfo />
                  </span>
                }>
                  <EdInput
                    value={rule.id ?? ''}
                    onChange={(v) => dispatch({ type: 'SET_PATH', path: `entry.rules.${ri}.id`, value: v })}
                    type="text"
                    placeholder="e.g., RSI Entry"
                  />
                </EditorField>

                <EditorField label={
                  <span className="flex items-center gap-1">
                    Direction
                    <DirectionInfo />
                  </span>
                }>
                  <StyledSelect
                    value={rule.direction}
                    onChange={(v) => dispatch({
                      type: 'CHANGE_DIRECTION',
                      ruleIndex: ri,
                      direction: v as 'BUY' | 'SELL' | 'SIGNAL',
                    })}
                    options={[
                      { value: 'BUY', label: 'BUY' },
                      { value: 'SELL', label: 'SELL' },
                      { value: 'SIGNAL', label: 'SIGNAL (dynamic)' },
                    ]}
                    className="!w-[150px]"
                  />
                </EditorField>

                <EditorField label={
                  <span className="flex items-center gap-1">
                    Order Type
                    <OrderTypeInfo />
                  </span>
                }>
                  <StyledSelect
                    value={rule.orderType ?? 'MARKET'}
                    onChange={(v) => dispatch({ type: 'SET_PATH', path: `entry.rules.${ri}.orderType`, value: v })}
                    options={[
                      { value: 'MARKET', label: 'Market' },
                      { value: 'LIMIT', label: 'Limit' },
                    ]}
                    className="!w-[150px]"
                  />
                </EditorField>

                {rule.orderType === 'LIMIT' && (
                  <EditorField label={
                    <span className="flex items-center gap-1">
                      Limit Offset
                      <LimitOffsetInfo />
                    </span>
                  }>
                    <EdInput
                      value={rule.limitOffsetPips ?? 0}
                      onChange={(v) => dispatch({ type: 'SET_PATH_NUM', path: `entry.rules.${ri}.limitOffsetPips`, value: v })}
                      min="0" step="0.1" small
                    />
                    <span className="text-[10px] text-ink-4">pips</span>
                  </EditorField>
                )}

                <div className="text-[10px] font-bold text-ink-4 uppercase tracking-[0.5px] mt-2.5 mb-1.5">
                  Conditions
                </div>
                <div {...(ri === 0 ? { 'data-tour': 'set-condition' } : {})}>
                  <EnhancedConditionEditor
                    condition={rule.conditions}
                    path={`entry.rules.${ri}.conditions`}
                    dispatch={dispatch}
                    onOpenModal={openModal}
                  />
                </div>

                {rule.direction === 'SIGNAL' && (
                  <>
                    <div className="text-[10px] font-bold text-green uppercase tracking-[0.5px] mt-2.5 mb-1.5">
                      Buy Condition
                    </div>
                    <EnhancedConditionEditor
                      condition={rule.buyCondition}
                      path={`entry.rules.${ri}.buyCondition`}
                      dispatch={dispatch}
                      onOpenModal={openModal}
                    />

                    <div className="text-[10px] font-bold text-red uppercase tracking-[0.5px] mt-2.5 mb-1.5">
                      Sell Condition
                    </div>
                    <EnhancedConditionEditor
                      condition={rule.sellCondition}
                      path={`entry.rules.${ri}.sellCondition`}
                      dispatch={dispatch}
                      onOpenModal={openModal}
                    />
                  </>
                )}

                {/* Live Preview */}
                <RulePreview condition={rule.conditions} />
                {rule.direction === 'SIGNAL' && rule.buyCondition && (
                  <div className="mt-1">
                    <span className="text-[9px] font-bold text-green mr-1">BUY:</span>
                    <RulePreview condition={rule.buyCondition} />
                  </div>
                )}
                {rule.direction === 'SIGNAL' && rule.sellCondition && (
                  <div className="mt-1">
                    <span className="text-[9px] font-bold text-red mr-1">SELL:</span>
                    <RulePreview condition={rule.sellCondition} />
                  </div>
                )}

                <div {...(ri === 0 ? { 'data-tour': 'lot-sizing' } : {})} className="mt-3">
                  <div className="text-[10px] font-bold text-ink-4 uppercase tracking-[0.5px] mb-1.5">
                    Lot Sizing
                  </div>
                  <LotSizingEditor
                    lot={rule.lotSizing}
                    path={`entry.rules.${ri}.lotSizing`}
                    dispatch={dispatch}
                  />
                </div>

                <EditorField label={
                  <span className="flex items-center gap-1">
                    Cooldown
                    <CooldownInfo />
                  </span>
                }>
                  <EdInput
                    value={rule.cooldownCandles ?? 0}
                    onChange={(v) => dispatch({ type: 'SET_PATH_NUM', path: `entry.rules.${ri}.cooldownCandles`, value: v })}
                    min="0" step="1" small
                  />
                  <span className="text-[10px] text-ink-4">candles</span>
                </EditorField>

                <EditorField label={
                  <span className="flex items-center gap-1">
                    Max Positions
                    <MaxPositionsInfo />
                  </span>
                }>
                  <EdInput
                    value={rule.maxPositions ?? ''}
                    onChange={(v) => dispatch({ type: 'SET_PATH_NUM', path: `entry.rules.${ri}.maxPositions`, value: v })}
                    min="1" step="1" small placeholder="No limit"
                  />
                </EditorField>
              </div>
            );
          })}

          <button
            type="button"
            onClick={() => dispatch({ type: 'ADD_ENTRY_RULE' })}
            className="flex items-center justify-center gap-1.5 py-1.5 px-3.5 rounded-[10px] border border-dashed border-orange/20 text-ink-4 hover:border-orange hover:text-orange text-[10px] font-bold w-full cursor-pointer transition-colors"
          >
            + Add Entry Rule
          </button>
        </AccordionSection>

        {/* Grid Configuration */}
        <AccordionSection
          title="Grid Configuration"
          icon={<Grid3x3 size={14} />}
          iconColor="text-violet"
          isOpen={sections.grid}
          onToggle={() => toggle('grid')}
          dataTour="grid-section"
        >
          <GridSection draft={draft} dispatch={dispatch} />
        </AccordionSection>

        {/* Exit Rules */}
        <AccordionSection
          title="Exit Rules"
          icon={<XCircle size={14} />}
          iconColor="text-red"
          isOpen={sections.exit}
          onToggle={() => toggle('exit')}
          dataTour="exit-rules"
        >
          {draft.exit.rules.map((rule, ri) => {
            return (
              <div
                key={ri}
                className="rounded-[12px] p-3 border mb-2.5 glass-sm border-glass-border"
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[11px] font-extrabold text-ink-2">
                    {rule.name || rule.id || `Rule ${ri + 1}`}
                  </span>
                  {draft.exit.rules.length > 1 && (
                    <button
                      type="button"
                      onClick={() => dispatch({ type: 'REMOVE_EXIT_RULE', index: ri })}
                      className="text-[10px] font-bold text-red/70 hover:text-red cursor-pointer transition-colors"
                    >
                      Remove
                    </button>
                  )}
                </div>

                <EditorField label={
                  <span className="flex items-center gap-1">
                    Name
                    <ExitNameInfo />
                  </span>
                }>
                  <EdInput
                    value={rule.name ?? ''}
                    onChange={(v) => dispatch({ type: 'SET_PATH', path: `exit.rules.${ri}.name`, value: v })}
                    type="text"
                  />
                </EditorField>

                <div className="text-[10px] font-bold text-ink-4 uppercase tracking-[0.5px] mt-2.5 mb-1.5">
                  Conditions
                </div>
                <EnhancedConditionEditor
                  condition={rule.conditions}
                  path={`exit.rules.${ri}.conditions`}
                  dispatch={dispatch}
                  onOpenModal={openModal}
                />

                {/* Live Preview */}
                <RulePreview condition={rule.conditions} />

                <div className="mt-3">
                  <EditorField label={
                    <span className="flex items-center gap-1">
                      Action
                      <ExitActionInfo />
                    </span>
                  }>
                    <StyledSelect
                      value={rule.action}
                      onChange={(v) => dispatch({ type: 'SET_PATH', path: `exit.rules.${ri}.action`, value: v })}
                      options={[
                        { value: 'CLOSE_ALL', label: 'Close All Positions' },
                        { value: 'CLOSE_OLDEST', label: 'Close Oldest' },
                        { value: 'CLOSE_NEWEST', label: 'Close Newest' },
                        { value: 'CLOSE_PARTIAL', label: 'Close Partial' },
                      ]}
                      className="!w-[180px]"
                    />
                  </EditorField>
                </div>
                {rule.action === 'CLOSE_PARTIAL' && (
                  <EditorField label={
                    <span className="flex items-center gap-1">
                      Close %
                      <ClosePctInfo />
                    </span>
                  }>
                    <EdInput
                      value={rule.closePct ?? 50}
                      onChange={(v) => dispatch({ type: 'SET_PATH_NUM', path: `exit.rules.${ri}.closePct`, value: v })}
                      min="1" max="100" step="1" small
                    />
                    <span className="text-[10px] text-ink-4 ml-1">% of each position</span>
                  </EditorField>
                )}

                {/* Scope to entry rules */}
                <div className="mt-3">
                  <EditorField label={
                    <span className="flex items-center gap-1">
                      Applies to
                      <AppliesToInfo />
                    </span>
                  }>
                    <div className="flex flex-wrap gap-1.5">
                      {[...draft.entry.rules.map((er) => er.id), ...(draft.entry.grid?.enabled ? ['GRID'] : [])].map((entryId) => {
                        const selected = rule.forEntryRules?.includes(entryId) ?? false;
                        return (
                          <button
                            key={entryId}
                            type="button"
                            onClick={() => {
                              const current = rule.forEntryRules ?? [];
                              const next = selected
                                ? current.filter((id: string) => id !== entryId)
                                : [...current, entryId];
                              dispatch({
                                type: 'SET_PATH',
                                path: `exit.rules.${ri}.forEntryRules`,
                                value: next.length > 0 ? next : undefined,
                              });
                            }}
                            className={`px-2 py-0.5 rounded-md text-[10px] font-bold border transition-colors cursor-pointer ${
                              selected
                                ? 'border-orange bg-orange/10 text-orange'
                                : 'border-glass-border text-ink-4 hover:border-ink-4'
                            }`}
                          >
                            {entryId}
                          </button>
                        );
                      })}
                    </div>
                    <span className="text-[9px] text-ink-4 mt-1">
                      {rule.forEntryRules?.length
                        ? 'Only closes positions from selected entries'
                        : 'All positions (click to scope)'}
                    </span>
                  </EditorField>
                </div>
              </div>
            );
          })}

          <button
            type="button"
            onClick={() => dispatch({ type: 'ADD_EXIT_RULE' })}
            className="flex items-center justify-center gap-1.5 py-1.5 px-3.5 rounded-[10px] border border-dashed border-orange/20 text-ink-4 hover:border-orange hover:text-orange text-[10px] font-bold w-full cursor-pointer transition-colors"
          >
            + Add Exit Rule
          </button>
        </AccordionSection>

        {/* Trailing Stop */}
        <AccordionSection
          title="Trailing Stop"
          icon={<Crosshair size={14} />}
          iconColor="text-amber"
          isOpen={sections.trailing}
          onToggle={() => toggle('trailing')}
          dataTour="trailing-section"
        >
          <TrailingStopSection draft={draft} dispatch={dispatch} />
        </AccordionSection>

        {/* Risk Management */}
        <AccordionSection
          title="Risk Management"
          icon={<Shield size={14} />}
          iconColor="text-cyan"
          isOpen={sections.risk}
          onToggle={() => toggle('risk')}
          dataTour="risk-section"
        >
          <EditorField label={
            <span className="flex items-center gap-1">
              Equity SL %
              <EquitySlInfo />
            </span>
          }>
            <EdInput
              value={draft.exit.equityStopLossPct}
              onChange={(v) => dispatch({ type: 'SET_PATH_NUM', path: 'exit.equityStopLossPct', value: v })}
              step="1" min="1" max="100" small
            />
          </EditorField>
          <EditorField label={
            <span className="flex items-center gap-1">
              Risk Factor
              <RiskFactorInfo />
            </span>
          }>
            <EdInput
              value={draft.risk.riskFactor}
              onChange={(v) => dispatch({ type: 'SET_PATH_NUM', path: 'risk.riskFactor', value: v })}
              step="any" small
            />
          </EditorField>
          <EditorField label={
            <span className="flex items-center gap-1">
              Max Positions
              <GlobalMaxPositionsInfo />
            </span>
          }>
            <EdInput
              value={draft.risk.maxTotalPositions}
              onChange={(v) => dispatch({ type: 'SET_PATH_NUM', path: 'risk.maxTotalPositions', value: v })}
              min="1" step="1" small
            />
          </EditorField>
        </AccordionSection>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red/10 border border-red/20 rounded-lg px-3 py-2">
          <p className="text-[11px] text-red font-semibold">{error}</p>
        </div>
      )}
      {hasOffsetError && (
        <div className="bg-red/10 border border-red/20 rounded-lg px-3 py-2">
          <p className="text-[11px] text-red font-semibold">Offset exceeds maximum of {MAX_OFFSET} candles</p>
        </div>
      )}

      {/* Action */}
      <Button className="w-full" onClick={handleSave} disabled={saving || hasOffsetError}>
        {saving ? <Spinner size="sm" /> : <Save size={14} />}
        {isEdit ? 'Save Changes' : 'Create Strategy'}
      </Button>
    </div>

    {/* Chat Panel (right sidebar) */}
    <StrategyChatPanel
      currentRules={draft}
      symbol={symbol}
      onApplyRules={handleApplyRulesFromChat}
      onSymbolChange={(s) => setSymbol(s)}
      isOpen={chatOpen}
      onClose={() => setChatOpen(false)}
      isEdit={isEdit}
      templateId={templateId}
      strategyId={strategyId}
      strategyName={name}
      strategyDescription={description}
    />
    </div>
    </div>
  );
}

// ─── Template metadata ──────────────────────────────

const TEMPLATE_META: Record<string, { icon: ReactNode; tag: string; tagColor: string }> = {
  'martingale-grid': { icon: <Layers size={18} className="text-orange" />, tag: 'Grid', tagColor: 'text-orange bg-orange-bg' },
  'reverse-martingale-grid': { icon: <TrendingDown size={18} className="text-violet" />, tag: 'Grid', tagColor: 'text-orange bg-orange-bg' },
  'conservative-grid': { icon: <Box size={18} className="text-green" />, tag: 'Grid', tagColor: 'text-orange bg-orange-bg' },
  'macd-crossover': { icon: <BarChart3 size={18} className="text-blue" />, tag: 'Trend', tagColor: 'text-blue bg-blue-bg' },
  'london-session-scalper': { icon: <Clock size={18} className="text-amber" />, tag: 'Scalper', tagColor: 'text-amber bg-amber-bg' },
  'cci-mean-reversion': { icon: <RefreshCcw size={18} className="text-pink" />, tag: 'Reversal', tagColor: 'text-pink bg-pink-bg' },
  'scaled-exit-trend': { icon: <ScissorsLineDashed size={18} className="text-emerald" />, tag: 'Trend', tagColor: 'text-blue bg-blue-bg' },
  'from-scratch': { icon: <Code size={18} className="text-cyan" />, tag: 'Custom', tagColor: 'text-cyan bg-cyan-bg' },
};

// ─── Template Picker ─────────────────────────────────

function TemplatePicker({
  onSelect,
  onAICreate,
  isCreating,
}: {
  onSelect: (tpl: StrategyTemplatePreset) => void;
  onAICreate: () => void;
  isCreating?: boolean;
}) {
  return (
    <div className="space-y-8 max-w-2xl mx-auto">
      <div>
        <Link
          to="/strategies"
          className="inline-flex items-center gap-1.5 text-[10px] font-semibold text-ink-3 hover:text-orange transition-colors mb-4"
        >
          <ArrowLeft size={12} />
          All Strategies
        </Link>
        <h1 className="text-2xl font-extrabold tracking-tight">Create a Strategy</h1>
        <p className="text-sm text-ink-3 mt-1">Start with AI, pick a template, or build from scratch.</p>
      </div>

      <div className={isCreating ? 'opacity-50 pointer-events-none' : ''}>
        {/* AI Chat — hero CTA */}
        <button
          onClick={onAICreate}
          disabled={isCreating}
          className="w-full glass-sm rounded-xl p-6 text-left cursor-pointer transition-all border border-orange/20 hover:border-orange/40 hover:shadow-glow group mb-8"
        >
          <div className="flex items-start gap-4">
            <div className="w-11 h-11 rounded-lg bg-orange/10 flex items-center justify-center flex-shrink-0 group-hover:bg-orange/20 transition-colors mt-0.5">
              <Bot size={22} className="text-orange" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <h2 className="text-base font-extrabold group-hover:text-orange transition-colors">
                  Build with AI Chat
                </h2>
                <Sparkles size={14} className="text-orange/50" />
              </div>
              <p className="text-xs text-ink-3 leading-relaxed mb-3">
                Describe your trading idea in plain English — the AI will generate entry rules, exit rules, risk management, and all the configuration for you.
              </p>
              <span className="inline-flex items-center gap-1.5 text-[10px] font-bold text-orange">
                <MessageSquare size={11} />
                Start chatting
                <ArrowLeft size={10} className="rotate-180" />
              </span>
            </div>
          </div>
        </button>

        {/* Upload trade history */}
        <Link
          to="/strategies/new/upload"
          className="w-full glass-sm rounded-xl p-5 text-left transition-all border border-glass-border hover:border-orange/30 group mb-8 flex items-start gap-4"
        >
          <div className="w-11 h-11 rounded-lg bg-surface-2 flex items-center justify-center flex-shrink-0 group-hover:bg-orange-bg transition-colors mt-0.5">
            <Upload size={20} className="text-ink-3 group-hover:text-orange transition-colors" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-base font-extrabold group-hover:text-orange transition-colors mb-1">
              Upload Trade History
            </h2>
            <p className="text-xs text-ink-3 leading-relaxed">
              Import your MT5 trade history and let the AI reverse-engineer a strategy from your winning patterns.
            </p>
          </div>
        </Link>

        {/* Templates */}
        <div className="space-y-3">
          <p className="text-[10px] font-bold text-ink-4 uppercase tracking-wider">Or pick a template</p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            {STRATEGY_TEMPLATES.map((tpl) => {
              const meta = TEMPLATE_META[tpl.id];
              return (
                <button
                  key={tpl.id}
                  onClick={() => onSelect(tpl)}
                  className="glass-sm rounded-lg p-4 text-left cursor-pointer transition-all hover:border-orange/30 border border-glass-border group flex items-start gap-3"
                >
                  <div className="w-9 h-9 rounded-md bg-surface-2 flex items-center justify-center flex-shrink-0 group-hover:bg-orange-bg transition-colors mt-0.5">
                    {meta?.icon ?? <Code size={18} className="text-ink-3" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-xs font-bold group-hover:text-orange transition-colors truncate mb-0.5">
                      {tpl.name}
                    </h3>
                    <p className="text-[10px] text-ink-3 leading-relaxed line-clamp-2">{tpl.description}</p>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Order Type Info Modal ───────────────────────────

function TimeframeInfo() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} className="cursor-pointer">
        <HelpCircle size={11} className="text-ink-4 hover:text-orange transition-colors" />
      </button>
      <Modal open={open} onClose={() => setOpen(false)} title="Timeframe">
        <div className="space-y-4 text-xs text-ink-2 leading-relaxed">
          <p>The candle interval used for indicator calculations and strategy evaluation. Each candle represents one period of this duration.</p>

          <div className="h-px bg-border" />

          <div>
            <h3 className="text-[11px] font-bold text-ink-1 mb-1">Common Timeframes</h3>
            <div className="space-y-1.5">
              <div className="bg-surface rounded-lg px-3 py-2 flex items-center justify-between">
                <span className="text-[10px] font-bold text-ink-3">1m – 5m</span>
                <span className="text-[10px] text-ink-4">Scalping — fast signals, more noise, higher trade frequency</span>
              </div>
              <div className="bg-surface rounded-lg px-3 py-2 flex items-center justify-between">
                <span className="text-[10px] font-bold text-ink-3">15m – 1h</span>
                <span className="text-[10px] text-ink-4">Day trading — balanced signal quality and frequency</span>
              </div>
              <div className="bg-surface rounded-lg px-3 py-2 flex items-center justify-between">
                <span className="text-[10px] font-bold text-ink-3">4h – 1d</span>
                <span className="text-[10px] text-ink-4">Swing trading — stronger signals, fewer trades</span>
              </div>
            </div>
          </div>

          <div className="h-px bg-border" />

          <div>
            <h3 className="text-[11px] font-bold text-ink-1 mb-1">Impact on strategy</h3>
            <ul className="list-disc list-inside space-y-1 text-ink-3">
              <li>Indicator values (RSI, MACD, etc.) are computed from candles of this size</li>
              <li>Cooldown periods are measured in candles of this timeframe</li>
              <li>Shorter timeframes = more signals but more false positives</li>
              <li>Longer timeframes = fewer signals but higher quality</li>
            </ul>
          </div>

          <p className="text-ink-4">Grid/martingale strategies typically use <strong className="text-ink-3">1m</strong> for responsive grid fills. Trend strategies work better on <strong className="text-ink-3">15m+</strong>.</p>
        </div>
      </Modal>
    </>
  );
}

function EntryNameInfo() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} className="cursor-pointer">
        <HelpCircle size={11} className="text-ink-4 hover:text-orange transition-colors" />
      </button>
      <Modal open={open} onClose={() => setOpen(false)} title="Entry Rule Name">
        <div className="space-y-4 text-xs text-ink-2 leading-relaxed">
          <p>A unique identifier for this entry rule. The name is used throughout the system to reference this specific rule.</p>

          <div className="h-px bg-border" />

          <div>
            <h3 className="text-[11px] font-bold text-ink-1 mb-1">Where it appears</h3>
            <ul className="list-disc list-inside space-y-1 text-ink-3">
              <li>In exit rules' <strong className="text-ink-2">"Applies to"</strong> selector to scope exits to specific entries</li>
              <li>In trade history to identify which rule opened each position</li>
              <li>In backtest results and performance breakdown</li>
            </ul>
          </div>

          <div className="bg-surface rounded-lg px-3 py-2">
            <span className="text-[10px] font-bold text-ink-3">Examples</span>
            <p className="text-[10px] text-ink-4 mt-0.5">"RSI Oversold", "MACD Cross", "Grid DCA", "London Open"</p>
          </div>

          <p className="text-ink-4">Keep names short and descriptive. If you have multiple entry rules, distinct names help you track which rule is performing best.</p>
        </div>
      </Modal>
    </>
  );
}

function LimitOffsetInfo() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} className="cursor-pointer">
        <HelpCircle size={11} className="text-ink-4 hover:text-orange transition-colors" />
      </button>
      <Modal open={open} onClose={() => setOpen(false)} title="Limit Offset (Pips)">
        <div className="space-y-4 text-xs text-ink-2 leading-relaxed">
          <p>How far from the current price (in pips) the pending limit order is placed. A larger offset means waiting for a better price but less chance of being filled.</p>

          <div className="space-y-2">
            <div className="bg-surface rounded-lg px-3 py-2">
              <span className="text-[10px] font-bold text-green">BUY Limit</span>
              <p className="text-[10px] text-ink-3 mt-0.5">Placed <strong className="text-ink-2">below</strong> current ask. Offset 5 = order at ask − 5 pips.</p>
            </div>
            <div className="bg-surface rounded-lg px-3 py-2">
              <span className="text-[10px] font-bold text-red">SELL Limit</span>
              <p className="text-[10px] text-ink-3 mt-0.5">Placed <strong className="text-ink-2">above</strong> current bid. Offset 5 = order at bid + 5 pips.</p>
            </div>
          </div>

          <div className="h-px bg-border" />

          <div>
            <h3 className="text-[11px] font-bold text-ink-1 mb-1">Suggested Offsets</h3>
            <div className="grid grid-cols-2 gap-1.5">
              <div className="bg-surface rounded-lg px-3 py-2">
                <span className="text-[10px] font-bold text-ink-3">Forex</span>
                <p className="text-[10px] text-ink-4">5 – 15 pips</p>
              </div>
              <div className="bg-surface rounded-lg px-3 py-2">
                <span className="text-[10px] font-bold text-ink-3">Gold</span>
                <p className="text-[10px] text-ink-4">50 – 200 pips</p>
              </div>
              <div className="bg-surface rounded-lg px-3 py-2">
                <span className="text-[10px] font-bold text-ink-3">Indices</span>
                <p className="text-[10px] text-ink-4">10 – 50 pips</p>
              </div>
              <div className="bg-surface rounded-lg px-3 py-2">
                <span className="text-[10px] font-bold text-ink-3">Crypto</span>
                <p className="text-[10px] text-ink-4">100 – 500 pips</p>
              </div>
            </div>
          </div>

          <p className="text-ink-4">Set to <strong className="text-ink-3">0</strong> to place the limit order at the exact current price (behaves like a market order).</p>
        </div>
      </Modal>
    </>
  );
}

function ExitNameInfo() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} className="cursor-pointer">
        <HelpCircle size={11} className="text-ink-4 hover:text-orange transition-colors" />
      </button>
      <Modal open={open} onClose={() => setOpen(false)} title="Exit Rule Name">
        <div className="space-y-4 text-xs text-ink-2 leading-relaxed">
          <p>A label for this exit rule. Helps you identify different exit conditions when you have multiple exit rules.</p>

          <div className="bg-surface rounded-lg px-3 py-2">
            <span className="text-[10px] font-bold text-ink-3">Examples</span>
            <p className="text-[10px] text-ink-4 mt-0.5">"Take Profit", "RSI Overbought Exit", "End of Session", "Drawdown Cut"</p>
          </div>

          <p className="text-ink-4">Optional but recommended when using multiple exit rules to keep your strategy organized.</p>
        </div>
      </Modal>
    </>
  );
}

function ClosePctInfo() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} className="cursor-pointer">
        <HelpCircle size={11} className="text-ink-4 hover:text-orange transition-colors" />
      </button>
      <Modal open={open} onClose={() => setOpen(false)} title="Close Percentage">
        <div className="space-y-4 text-xs text-ink-2 leading-relaxed">
          <p>The percentage of each position's volume to close when this exit rule triggers. Only used with the <strong className="text-ink-1">Close Partial</strong> action.</p>

          <div className="bg-surface rounded-lg px-3 py-2">
            <span className="text-[10px] font-bold text-ink-3">Example</span>
            <p className="text-[10px] text-ink-3 mt-0.5">Close % = <strong className="text-ink-2">50</strong>, position = 0.10 lots → closes <strong className="text-ink-1">0.05 lots</strong>, keeps 0.05 lots running.</p>
          </div>

          <div className="h-px bg-border" />

          <div>
            <h3 className="text-[11px] font-bold text-ink-1 mb-1">Use cases</h3>
            <div className="space-y-1.5">
              <div className="bg-surface rounded-lg px-3 py-2">
                <span className="text-[10px] font-bold text-ink-3">50% at target</span>
                <p className="text-[10px] text-ink-4">Lock in half the profit, let the rest run with a trailing stop.</p>
              </div>
              <div className="bg-surface rounded-lg px-3 py-2">
                <span className="text-[10px] font-bold text-ink-3">33% at each level</span>
                <p className="text-[10px] text-ink-4">Scale out in thirds at different profit targets.</p>
              </div>
              <div className="bg-surface rounded-lg px-3 py-2">
                <span className="text-[10px] font-bold text-ink-3">75% on reversal signal</span>
                <p className="text-[10px] text-ink-4">Reduce exposure but keep a small position in case the trend continues.</p>
              </div>
            </div>
          </div>

          <p className="text-ink-4">Range: <strong className="text-ink-3">1 – 100%</strong>. Use 100% for a full close (same as Close All for that position).</p>
        </div>
      </Modal>
    </>
  );
}

function AppliesToInfo() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} className="cursor-pointer">
        <HelpCircle size={11} className="text-ink-4 hover:text-orange transition-colors" />
      </button>
      <Modal open={open} onClose={() => setOpen(false)} title="Applies To (Entry Rule Scope)">
        <div className="space-y-4 text-xs text-ink-2 leading-relaxed">
          <p>Scopes this exit rule to only close positions opened by specific entry rules. By default, an exit rule closes positions from <strong className="text-ink-1">all</strong> entry rules.</p>

          <div className="bg-surface rounded-lg px-3 py-2">
            <span className="text-[10px] font-bold text-ink-3">Example</span>
            <p className="text-[10px] text-ink-3 mt-0.5">Entry rules: "RSI Entry" and "GRID". If you select only <strong className="text-orange">"RSI Entry"</strong>, this exit rule will only close positions from the RSI entry — grid positions remain open.</p>
          </div>

          <div className="h-px bg-border" />

          <div>
            <h3 className="text-[11px] font-bold text-ink-1 mb-1">When to scope exits</h3>
            <ul className="list-disc list-inside space-y-1 text-ink-3">
              <li>Different take-profit targets for different entry types</li>
              <li>Grid positions need separate exit logic from signal entries</li>
              <li>One entry type has a trailing stop, another has fixed TP</li>
            </ul>
          </div>

          <div className="h-px bg-border" />

          <p className="text-ink-4">Click entry rule names to toggle selection. <strong className="text-ink-3">No selection</strong> = applies to all positions.</p>
        </div>
      </Modal>
    </>
  );
}

function DirectionInfo() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} className="cursor-pointer">
        <HelpCircle size={11} className="text-ink-4 hover:text-orange transition-colors" />
      </button>
      <Modal open={open} onClose={() => setOpen(false)} title="Entry Direction">
        <div className="space-y-4 text-xs text-ink-2 leading-relaxed">
          <div>
            <h3 className="text-[11px] font-bold text-ink-1 mb-1">BUY</h3>
            <p>Only opens long (buy) positions when conditions are met. Use this when your strategy is designed to profit from upward price movement.</p>
            <div className="bg-surface rounded-lg px-3 py-2 mt-1.5">
              <p className="text-[10px] text-ink-3">Example: RSI drops below 30 → open a BUY position expecting a bounce up.</p>
            </div>
          </div>

          <div className="h-px bg-border" />

          <div>
            <h3 className="text-[11px] font-bold text-ink-1 mb-1">SELL</h3>
            <p>Only opens short (sell) positions when conditions are met. Use this when your strategy profits from downward price movement.</p>
            <div className="bg-surface rounded-lg px-3 py-2 mt-1.5">
              <p className="text-[10px] text-ink-3">Example: RSI rises above 70 → open a SELL position expecting a pullback down.</p>
            </div>
          </div>

          <div className="h-px bg-border" />

          <div>
            <h3 className="text-[11px] font-bold text-orange mb-1">SIGNAL (Dynamic)</h3>
            <p>Evaluates shared conditions first, then checks separate <strong className="text-green">Buy Condition</strong> and <strong className="text-red">Sell Condition</strong> to decide the trade direction dynamically.</p>
            <div className="bg-surface rounded-lg px-3 py-2 mt-1.5 space-y-1">
              <p className="text-[10px] text-ink-3"><strong className="text-ink-2">Step 1:</strong> Main conditions must be true (e.g., ADX &gt; 25)</p>
              <p className="text-[10px] text-ink-3"><strong className="text-green">Step 2a:</strong> If buy condition met → open BUY</p>
              <p className="text-[10px] text-ink-3"><strong className="text-red">Step 2b:</strong> If sell condition met → open SELL</p>
            </div>
            <p className="text-ink-3 mt-1.5">Best for: strategies that trade both directions using the same core logic, like MACD crossover or moving average systems.</p>
          </div>
        </div>
      </Modal>
    </>
  );
}

function CooldownInfo() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} className="cursor-pointer">
        <HelpCircle size={11} className="text-ink-4 hover:text-orange transition-colors" />
      </button>
      <Modal open={open} onClose={() => setOpen(false)} title="Cooldown Period">
        <div className="space-y-4 text-xs text-ink-2 leading-relaxed">
          <p>The minimum number of candles that must pass after opening a position before this entry rule can trigger again.</p>

          <div className="bg-surface rounded-lg px-3 py-2">
            <span className="text-[10px] font-bold text-ink-3">Example</span>
            <p className="text-[10px] text-ink-3 mt-0.5">On a <strong className="text-ink-2">1-minute</strong> timeframe with cooldown = <strong className="text-ink-2">5</strong>, after opening a trade you must wait at least 5 minutes before this rule can open another.</p>
          </div>

          <div className="h-px bg-border" />

          <div>
            <h3 className="text-[11px] font-bold text-ink-1 mb-1">Why use a cooldown?</h3>
            <ul className="list-disc list-inside space-y-1 text-ink-3">
              <li>Prevents rapid-fire entries when conditions stay true for multiple candles</li>
              <li>Reduces overtrading and unnecessary commission costs</li>
              <li>Gives previous positions time to develop before adding more</li>
            </ul>
          </div>

          <div className="h-px bg-border" />

          <div>
            <h3 className="text-[11px] font-bold text-ink-1 mb-1">Suggested Values</h3>
            <div className="grid grid-cols-2 gap-1.5">
              <div className="bg-surface rounded-lg px-3 py-2">
                <span className="text-[10px] font-bold text-ink-3">Scalping (1m–5m)</span>
                <p className="text-[10px] text-ink-4">3 – 10 candles</p>
              </div>
              <div className="bg-surface rounded-lg px-3 py-2">
                <span className="text-[10px] font-bold text-ink-3">Day Trading (15m–1h)</span>
                <p className="text-[10px] text-ink-4">2 – 5 candles</p>
              </div>
              <div className="bg-surface rounded-lg px-3 py-2">
                <span className="text-[10px] font-bold text-ink-3">Grid/Martingale</span>
                <p className="text-[10px] text-ink-4">0 – 1 candle</p>
              </div>
              <div className="bg-surface rounded-lg px-3 py-2">
                <span className="text-[10px] font-bold text-ink-3">Swing Trading</span>
                <p className="text-[10px] text-ink-4">5 – 20 candles</p>
              </div>
            </div>
          </div>

          <p className="text-ink-4">Set to <strong className="text-ink-3">0</strong> to disable cooldown (entry can trigger every candle).</p>
        </div>
      </Modal>
    </>
  );
}

function MaxPositionsInfo() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} className="cursor-pointer">
        <HelpCircle size={11} className="text-ink-4 hover:text-orange transition-colors" />
      </button>
      <Modal open={open} onClose={() => setOpen(false)} title="Max Positions (Per Rule)">
        <div className="space-y-4 text-xs text-ink-2 leading-relaxed">
          <p>The maximum number of positions this specific entry rule can have open at the same time. Once the limit is reached, the rule will not open any new positions until existing ones are closed.</p>

          <div className="bg-surface rounded-lg px-3 py-2">
            <span className="text-[10px] font-bold text-ink-3">Example</span>
            <p className="text-[10px] text-ink-3 mt-0.5">Max Positions = <strong className="text-ink-2">3</strong> → this rule can have at most 3 open trades. A 4th signal is ignored until one closes.</p>
          </div>

          <div className="h-px bg-border" />

          <div>
            <h3 className="text-[11px] font-bold text-ink-1 mb-1">Per-Rule vs Global</h3>
            <p>This limit applies only to <strong className="text-ink-1">this entry rule</strong>. The global Max Positions setting (in Risk Management) caps the total across all entry rules combined.</p>
            <div className="bg-surface rounded-lg px-3 py-2 mt-1.5 font-mono text-[10px] text-ink-3 space-y-0.5">
              <p>Rule A max: <strong className="text-ink-1">3</strong> → can open up to 3</p>
              <p>Rule B max: <strong className="text-ink-1">2</strong> → can open up to 2</p>
              <p>Global max: <strong className="text-ink-1">4</strong> → total across A+B capped at 4</p>
            </div>
          </div>

          <p className="text-ink-4">Leave empty for <strong className="text-ink-3">no limit</strong> (only constrained by global max positions).</p>
        </div>
      </Modal>
    </>
  );
}

function ExitActionInfo() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} className="cursor-pointer">
        <HelpCircle size={11} className="text-ink-4 hover:text-orange transition-colors" />
      </button>
      <Modal open={open} onClose={() => setOpen(false)} title="Exit Actions">
        <div className="space-y-4 text-xs text-ink-2 leading-relaxed">
          <p>What happens to your positions when the exit conditions are met.</p>

          <div>
            <h3 className="text-[11px] font-bold text-ink-1 mb-1">Close All</h3>
            <p>Closes every open position at once. The most common exit action — clean slate when conditions signal a reversal or target is reached.</p>
          </div>

          <div className="h-px bg-border" />

          <div>
            <h3 className="text-[11px] font-bold text-ink-1 mb-1">Close Oldest</h3>
            <p>Closes only the position that was opened first (FIFO). Other positions remain open.</p>
            <p className="text-ink-3 mt-1">Useful for: scaling out of grid/pyramid positions one at a time.</p>
          </div>

          <div className="h-px bg-border" />

          <div>
            <h3 className="text-[11px] font-bold text-ink-1 mb-1">Close Newest</h3>
            <p>Closes only the most recently opened position (LIFO). Other positions remain open.</p>
            <p className="text-ink-3 mt-1">Useful for: cutting losing add-on positions while keeping the original profitable entry.</p>
          </div>

          <div className="h-px bg-border" />

          <div>
            <h3 className="text-[11px] font-bold text-orange mb-1">Close Partial</h3>
            <p>Reduces each position's volume by a specified percentage rather than closing fully. Lets you lock in partial profits while keeping exposure.</p>
            <div className="bg-surface rounded-lg px-3 py-2 mt-1.5">
              <p className="text-[10px] text-ink-3">Example: Close % = <strong className="text-ink-2">50</strong> with 0.10 lot position → closes 0.05 lots, keeps 0.05 lots running.</p>
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
}

function EquitySlInfo() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} className="cursor-pointer">
        <HelpCircle size={11} className="text-ink-4 hover:text-orange transition-colors" />
      </button>
      <Modal open={open} onClose={() => setOpen(false)} title="Equity Stop Loss %">
        <div className="space-y-4 text-xs text-ink-2 leading-relaxed">
          <p>A safety net that stops the entire bot when its equity drops by this percentage from the assigned capital. This is a <strong className="text-ink-1">bot-level</strong> stop loss, not a per-trade SL.</p>

          <div className="bg-surface rounded-lg px-3 py-2">
            <span className="text-[10px] font-bold text-ink-3">Example</span>
            <p className="text-[10px] text-ink-3 mt-0.5">Assigned capital = <strong className="text-ink-2">$1,000</strong>, Equity SL = <strong className="text-ink-2">5%</strong> → if equity falls to <strong className="text-red">$950</strong>, the bot closes all positions and stops.</p>
          </div>

          <div className="h-px bg-border" />

          <div>
            <h3 className="text-[11px] font-bold text-ink-1 mb-1">How it works</h3>
            <ul className="list-disc list-inside space-y-1 text-ink-3">
              <li>Checked every tick — reacts in real-time to price changes</li>
              <li>Evaluates even with 0 open positions (realized losses count)</li>
              <li>When triggered: closes all positions, then stops the bot</li>
            </ul>
          </div>

          <div className="h-px bg-border" />

          <div>
            <h3 className="text-[11px] font-bold text-ink-1 mb-1">Suggested Values</h3>
            <div className="grid grid-cols-2 gap-1.5">
              <div className="bg-surface rounded-lg px-3 py-2">
                <span className="text-[10px] font-bold text-ink-3">Conservative</span>
                <p className="text-[10px] text-ink-4">1 – 3%</p>
              </div>
              <div className="bg-surface rounded-lg px-3 py-2">
                <span className="text-[10px] font-bold text-ink-3">Moderate</span>
                <p className="text-[10px] text-ink-4">5 – 10%</p>
              </div>
              <div className="bg-surface rounded-lg px-3 py-2">
                <span className="text-[10px] font-bold text-ink-3">Grid/Martingale</span>
                <p className="text-[10px] text-ink-4">15 – 30%</p>
              </div>
              <div className="bg-surface rounded-lg px-3 py-2">
                <span className="text-[10px] font-bold text-ink-3">Aggressive</span>
                <p className="text-[10px] text-ink-4">30 – 50%</p>
              </div>
            </div>
          </div>

          <p className="text-ink-4">Lower values provide more protection but may stop the bot prematurely during normal drawdowns.</p>
        </div>
      </Modal>
    </>
  );
}

function RiskFactorInfo() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} className="cursor-pointer">
        <HelpCircle size={11} className="text-ink-4 hover:text-orange transition-colors" />
      </button>
      <Modal open={open} onClose={() => setOpen(false)} title="Risk Factor">
        <div className="space-y-4 text-xs text-ink-2 leading-relaxed">
          <p>A global multiplier applied to all lot sizes calculated by the strategy. It scales your position sizing up or down without changing the strategy logic.</p>

          <div className="bg-surface rounded-lg px-3 py-2">
            <span className="text-[10px] font-bold text-ink-3">Example</span>
            <p className="text-[10px] text-ink-3 mt-0.5">Strategy calculates 0.10 lots, Risk Factor = <strong className="text-ink-2">0.5</strong> → actual trade = <strong className="text-ink-1">0.05 lots</strong> (half size).</p>
          </div>

          <div className="h-px bg-border" />

          <div>
            <h3 className="text-[11px] font-bold text-ink-1 mb-1">Common Values</h3>
            <div className="space-y-1.5">
              <div className="bg-surface rounded-lg px-3 py-2 flex items-center justify-between">
                <span className="text-[10px] font-bold text-ink-3">0.5</span>
                <span className="text-[10px] text-ink-4">Half size — more conservative</span>
              </div>
              <div className="bg-surface rounded-lg px-3 py-2 flex items-center justify-between">
                <span className="text-[10px] font-bold text-ink-3">1.0</span>
                <span className="text-[10px] text-ink-4">Default — no scaling</span>
              </div>
              <div className="bg-surface rounded-lg px-3 py-2 flex items-center justify-between">
                <span className="text-[10px] font-bold text-ink-3">1.5</span>
                <span className="text-[10px] text-ink-4">50% larger positions</span>
              </div>
              <div className="bg-surface rounded-lg px-3 py-2 flex items-center justify-between">
                <span className="text-[10px] font-bold text-ink-3">2.0</span>
                <span className="text-[10px] text-ink-4">Double size — aggressive</span>
              </div>
            </div>
          </div>

          <p className="text-ink-4">Use this to quickly adjust risk exposure when deploying the same strategy on different account sizes, without modifying individual lot sizing settings.</p>
        </div>
      </Modal>
    </>
  );
}

function GlobalMaxPositionsInfo() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} className="cursor-pointer">
        <HelpCircle size={11} className="text-ink-4 hover:text-orange transition-colors" />
      </button>
      <Modal open={open} onClose={() => setOpen(false)} title="Max Total Positions">
        <div className="space-y-4 text-xs text-ink-2 leading-relaxed">
          <p>The maximum number of positions the bot can have open at the same time, across <strong className="text-ink-1">all entry rules combined</strong>. No new trades will open once this limit is reached.</p>

          <div className="bg-surface rounded-lg px-3 py-2">
            <span className="text-[10px] font-bold text-ink-3">Example</span>
            <p className="text-[10px] text-ink-3 mt-0.5">Max Total Positions = <strong className="text-ink-2">10</strong> with a grid strategy → after 10 grid levels are filled, no more levels open even if the price keeps moving.</p>
          </div>

          <div className="h-px bg-border" />

          <div>
            <h3 className="text-[11px] font-bold text-ink-1 mb-1">Why limit total positions?</h3>
            <ul className="list-disc list-inside space-y-1 text-ink-3">
              <li>Controls maximum capital exposure and margin usage</li>
              <li>Prevents grid/martingale strategies from over-extending</li>
              <li>Reduces risk of margin call on the MT5 account</li>
            </ul>
          </div>

          <div className="h-px bg-border" />

          <div>
            <h3 className="text-[11px] font-bold text-ink-1 mb-1">Suggested Values</h3>
            <div className="grid grid-cols-2 gap-1.5">
              <div className="bg-surface rounded-lg px-3 py-2">
                <span className="text-[10px] font-bold text-ink-3">Single-entry</span>
                <p className="text-[10px] text-ink-4">1 – 3</p>
              </div>
              <div className="bg-surface rounded-lg px-3 py-2">
                <span className="text-[10px] font-bold text-ink-3">Multi-rule</span>
                <p className="text-[10px] text-ink-4">5 – 10</p>
              </div>
              <div className="bg-surface rounded-lg px-3 py-2">
                <span className="text-[10px] font-bold text-ink-3">Grid strategy</span>
                <p className="text-[10px] text-ink-4">10 – 20</p>
              </div>
              <div className="bg-surface rounded-lg px-3 py-2">
                <span className="text-[10px] font-bold text-ink-3">Aggressive grid</span>
                <p className="text-[10px] text-ink-4">20 – 50</p>
              </div>
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
}

function OrderTypeInfo() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} className="cursor-pointer">
        <HelpCircle size={11} className="text-ink-4 hover:text-orange transition-colors" />
      </button>
      <Modal open={open} onClose={() => setOpen(false)} title="Order Types">
        <div className="space-y-4 text-xs text-ink-2 leading-relaxed">
          <div>
            <h3 className="text-[11px] font-bold text-ink-1 mb-1">Market Order</h3>
            <p>Executes immediately at the best available price. Fast and guaranteed to fill, but you may experience slippage during high volatility.</p>
            <p className="text-ink-3 mt-1">Best for: trend-following, breakout strategies, and any setup where getting in quickly matters more than the exact price.</p>
          </div>

          <div className="h-px bg-border" />

          <div>
            <h3 className="text-[11px] font-bold text-ink-1 mb-1">Limit Order</h3>
            <p>Places a pending order at a specific price offset from the current market. The order only fills when the price reaches your level — giving you a better entry price.</p>
            <p className="text-ink-3 mt-1">Best for: grid strategies, martingale, and mean-reversion setups where you want entries at exact price levels instead of chasing the market.</p>
          </div>

          <div className="h-px bg-border" />

          <div>
            <h3 className="text-[11px] font-bold text-orange mb-1.5">How Limit Offset Works</h3>
            <p className="mb-2">The offset (in pips) determines how far from the current price the pending order is placed:</p>
            <div className="space-y-2">
              <div className="bg-surface rounded-lg px-3 py-2">
                <span className="text-[10px] font-bold text-green">BUY Limit</span>
                <p className="text-[10px] text-ink-3 mt-0.5">Placed <strong className="text-ink-2">below</strong> current ask price. If ask is 2,000 and offset is 5 pips, order goes at 1,999.50.</p>
              </div>
              <div className="bg-surface rounded-lg px-3 py-2">
                <span className="text-[10px] font-bold text-red">SELL Limit</span>
                <p className="text-[10px] text-ink-3 mt-0.5">Placed <strong className="text-ink-2">above</strong> current bid price. If bid is 2,000 and offset is 5 pips, order goes at 2,000.50.</p>
              </div>
            </div>
          </div>

          <div className="h-px bg-border" />

          <div>
            <h3 className="text-[11px] font-bold text-ink-1 mb-1.5">Suggested Offsets</h3>
            <div className="grid grid-cols-2 gap-1.5">
              <div className="bg-surface rounded-lg px-3 py-2">
                <span className="text-[10px] font-bold text-ink-3">Forex Majors</span>
                <p className="text-[10px] text-ink-4">5 – 15 pips</p>
              </div>
              <div className="bg-surface rounded-lg px-3 py-2">
                <span className="text-[10px] font-bold text-ink-3">Gold (XAUUSD)</span>
                <p className="text-[10px] text-ink-4">50 – 200 pips</p>
              </div>
              <div className="bg-surface rounded-lg px-3 py-2">
                <span className="text-[10px] font-bold text-ink-3">Indices</span>
                <p className="text-[10px] text-ink-4">10 – 50 pips</p>
              </div>
              <div className="bg-surface rounded-lg px-3 py-2">
                <span className="text-[10px] font-bold text-ink-3">Crypto (BTC)</span>
                <p className="text-[10px] text-ink-4">100 – 500 pips</p>
              </div>
            </div>
          </div>

          <div className="h-px bg-border" />

          <div>
            <h3 className="text-[11px] font-bold text-ink-1 mb-1.5">Example: Grid + Limit Orders</h3>
            <p className="mb-2">A gold grid strategy with 3 levels, $5 step, and limit orders at 50 pip offset:</p>
            <div className="bg-surface rounded-lg px-3 py-2.5 font-mono text-[10px] text-ink-3 space-y-0.5">
              <p>Gold price: <strong className="text-ink-1">$2,000.00</strong></p>
              <p>Level 1 BUY limit: <strong className="text-green">$1,999.50</strong> <span className="text-ink-4">(50 pip offset)</span></p>
              <p>Level 2 BUY limit: <strong className="text-green">$1,994.50</strong> <span className="text-ink-4">(grid step down)</span></p>
              <p>Level 3 BUY limit: <strong className="text-green">$1,989.50</strong> <span className="text-ink-4">(grid step down)</span></p>
            </div>
            <p className="text-ink-3 mt-2">Each grid level places a pending order instead of market-buying — reducing slippage and getting better average entry prices.</p>
          </div>
        </div>
      </Modal>
    </>
  );
}

// ─── Page Component (handles routing + data loading) ─────

export default function StrategyFormPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const createStrategy = useCreateStrategy();

  const { data: strategy, isLoading } = useStrategy(id ?? '');

  // Loading state
  if (id && isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  if (id && !strategy) {
    return (
      <div className="glass-sm rounded-lg p-6 border-red/20 text-center max-w-2xl mx-auto">
        <p className="text-red text-sm font-semibold mb-2">Strategy not found</p>
        <Link to="/strategies" className="inline-block mt-3">
          <Button variant="ghost" size="sm">
            <ArrowLeft size={14} />
            Back to Strategies
          </Button>
        </Link>
      </div>
    );
  }

  // Create mode: show template picker, then immediately create a DRAFT and redirect to edit
  if (!id) {
    const handleSelectTemplate = async (tpl: StrategyTemplatePreset, openChat = false) => {
      try {
        const result = await createStrategy.mutateAsync({
          name: tpl.defaultName || 'Untitled Strategy',
          symbol: (tpl.rules as any).symbol || 'XAUUSD',
          description: tpl.defaultDescription || '',
          strategy_type: 'CONDITION_ENGINE',
          rules: tpl.rules,
        });
        navigate(`/strategies/${result.id}/edit${openChat ? '?chat=1' : ''}`, { replace: true });
      } catch {
        // Stay on picker if creation fails
      }
    };

    return (
      <TemplatePicker
        onSelect={(tpl) => handleSelectTemplate(tpl)}
        onAICreate={() => {
          const scratch = STRATEGY_TEMPLATES.find((t) => t.id === 'from-scratch');
          if (scratch) handleSelectTemplate(scratch, true);
        }}
        isCreating={createStrategy.isPending}
      />
    );
  }

  // Edit mode — normalize rules from API (MCP-created strategies may use flat format)
  const rawRules = strategy!.rules as any;
  const initialRules: ConditionEngineRules = {
    strategy_type: rawRules?.strategy_type || 'CONDITION_ENGINE',
    market_data: rawRules?.market_data || { timeframe: rawRules?.timeframe || '1h' },
    entry: Array.isArray(rawRules?.entry) ? { rules: rawRules.entry } : (rawRules?.entry || { rules: [] }),
    exit: Array.isArray(rawRules?.exit) ? { rules: rawRules.exit } : (rawRules?.exit || { rules: [], equityStopLossPct: 50 }),
    risk: rawRules?.risk || { riskFactor: 1, maxTotalPositions: 10 },
  };
  const initialMeta = { name: strategy!.name, symbol: strategy!.symbol ?? '', description: strategy!.description ?? '' };
  const formKey = `${id}-${strategy!.updated_at}`;

  // Check if we should auto-open chat (from AI create redirect)
  const searchParams = new URLSearchParams(window.location.search);
  const initialChatOpen = searchParams.get('chat') === '1';

  return (
    <StrategyForm
      key={formKey}
      initialRules={initialRules}
      initialMeta={initialMeta}
      isEdit={true}
      strategyId={id}
      initialChatOpen={initialChatOpen}
    />
  );
}
